import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './dashboard.css';
import { useAuth } from '../components/auth/AuthContext';
import { KeycloakService, fetchWithAutoRefresh } from 'auth/KeycloakService';
import axios from 'axios';
import ResponsiveWrapper from '../components/vm/ResponsiveWrapper';
import * as Sentry from "@sentry/react";
import { useNotification } from '../Notifications';

import CreditsModal from '../payment/CreditsModal';

import CreateVolumeModal from '../components/Volumes/CreateVolumeModal';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import { Check, Info, AlertTriangle, ChevronRight, Power, Circle, ChevronDown, Maximize2, Database, Eye, Moon, Trash2, Camera, CreditCard, Plus, Server, Key, Folder, Image, LogOut } from 'lucide-react';

import { 
  API_BASE_URL,
  GPU_PRICING,
  logError,
  logInfo,
  logUserAction,
  logPerformance,
  currencyUtils,
  ERROR_MESSAGES,
  sanitizeText,
  validateJupyterPassword,
  validateCloudInitScript,
  validateSnapshotName,
  getSafeErrorMessage,
  validateErrorObject,
  useResponsive,
  getVMOperationCache,
  clearVMOperationCache,
  getNetworkErrorMessage
} from './dashboardUtils';

import {
  SnapshotConfirmationModal,
  HibernateConfirmationModal,
  RebootConfirmationModal,
  StartConfirmationModal,
  StopConfirmationModal,
  RestoreConfirmationModal,
  ConfirmationModal,
  VMDetailsSection,
  VMSelectorSection,
  VMActionsSection,
  VMPricingSection,
  AccountInfoSection,
  QuickActionsSection,
  LogoutSection,
  BarrackVMCard,
  ActivityMessage
} from './DashboardComponents';

import VMDetailsPage from './VMDetailsPage';

const truncateText = (text, maxLength = 1000) => {
  if (!text || typeof text !== 'string') return '';
  return text.substring(0, maxLength);
};

// Helper function to detect advanced regions
const isAdvancedRegion = (vm) => {
  const advancedRegions = [
    "FINLAND-1", "FINLAND-2", "FINLAND-3",
    "FIN-01", "FIN-02", "FIN-03", "ICE-01"
  ];
  return advancedRegions.includes(vm?.region);
};

const canStartVM = (vm) => {
  if (!vm) return false;
  return ['SHUTOFF'].includes(vm.status);
};

const canStopVM = (vm) => {
  if (!vm) return false;
  return ['RUNNING'].includes(vm.status);
};

const canRestoreVM = (vm) => {
  if (!vm || !vm.status) return false;
  const isAdvanced = isAdvancedRegion(vm);
  const status = vm.status;
  return !isAdvanced && status === 'HIBERNATED';
};

const canRebootVM = (vm) => {
  if (!vm) return false;
  if (isAdvancedRegion(vm)) return false;
  return ['RUNNING'].includes(vm.status);
};

const canHibernateVM = (vm) => {
  if (!vm) return false;
  return ['RUNNING', 'SHUTOFF'].includes(vm.status);
};

const canDeleteVM = (vm) => {
  if (!vm) return false;
  return ['RUNNING', 'SHUTOFF', 'HIBERNATED'].includes(vm.status);
};

// Main Dashboard Component
const Dashboard = ({ userEmail, barrackId: propBarrackId }) => {
    const navigate = useNavigate();
    const { isMobile, isTablet, getGridClasses, getStatsCardClasses } = useResponsive();
    const notification = useNotification();

    const { isAuthenticated, isInitialized, isLoading: authLoading, handleAuthError } = useAuth();
    
    const [vms, setVMs] = useState([]);
    
    const [selectedVmForHibernate, setSelectedVmForHibernate] = useState(null);
    const [selectedVmForRestore, setSelectedVmForRestore] = useState(null);
    const [selectedVmForStart, setSelectedVmForStart] = useState(null);
    const [selectedVmForStop, setSelectedVmForStop] = useState(null);
    const [selectedVmForReboot, setSelectedVmForReboot] = useState(null);
    const [selectedVmForSnapshot, setSelectedVmForSnapshot] = useState(null);
    
    const [loadingStates, setLoadingStates] = useState({
        hibernating: new Set(),
        restoring: new Set(),
        starting: new Set(),
        stopping: new Set(),
        rebooting: new Set(),
        deleting: new Set(),
        creatingSnapshot: new Set()
    });
    
    const operationRefs = useRef({
        starting: new Set(),
        stopping: new Set(),
        rebooting: new Set(),
        hibernating: new Set(),
        restoring: new Set(),
        deleting: new Set(),
        creatingSnapshot: new Set()
    });
    
    const [lockedVMs, setLockedVMs] = useState(new Set());
    
    const [actionMessage, setActionMessage] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [lastShownActionError, setLastShownActionError] = useState('');
    const [preferredCurrency, setPreferredCurrency] = useState('USD');
    const [selectedVM, setSelectedVM] = useState(null);
    const [showVMDetails, setShowVMDetails] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastShownError, setLastShownError] = useState('');
    const [activities, setActivities] = useState(() => {
        const stored = localStorage.getItem('activities');
        return stored ? JSON.parse(stored) : [];
    });
    const [barrackId, setBarrackId] = useState(propBarrackId || 'Not Available');
    const [showCreditsModal, setShowCreditsModal] = useState(false);
    const [creditBalance, setCreditBalance] = useState({ 
        inr: 0, 
        usd: 0,
        eur: 0,
        locked_inr: 0,
        locked_usd: 0,
        locked_eur: 0 
    });
    const [userCountry, setUserCountry] = useState(null);
    const [userCurrency, setUserCurrency] = useState('USD');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeleteNotification, setShowDeleteNotification] = useState(false);
    const [selectedVmForDetails, setSelectedVmForDetails] = useState(null);
    const [vmSelectorDropdownOpen, setVmSelectorDropdownOpen] = useState(false);
    const [creditBalanceError, setCreditBalanceError] = useState(null);
    const [showCreateVolumeModal, setShowCreateVolumeModal] = useState(false);
const [regions, setRegions] = useState(['CANADA-1', 'FINLAND-1', 'FINLAND-2', 'FINLAND-3']);
    const [osImages, setOsImages] = useState([]);
    const [existingVolumeNames, setExistingVolumeNames] = useState([]);
    const [existingSnapshotNames, setExistingSnapshotNames] = useState([]);

    const isFetchingProfile = useRef(false);
    const isFetchingVMs = useRef(false);
    const isFetchingBalance = useRef(false);
    const isGettingBarrackId = useRef(false);
    const fetchVMsTimeoutRef = useRef(null);
    const eventSourceRef = useRef(null);
    const abortControllerRef = useRef(null);
    const deploymentTimeoutRef = useRef(null);
    const eventSourceRetries = useRef(0);
    
    const maxRetries = 3;
    const retryAttempts = useRef(0);

    useEffect(() => {
      if (error && error !== lastShownError) {
        notification.error(error);
        setLastShownError(error);
        setError(null);
      }
    }, [error, notification, lastShownError]);

    useEffect(() => {
      if (actionError && actionError !== lastShownActionError) {
        notification.error(actionError);
        setLastShownActionError(actionError);
        setActionError(null);
      }
    }, [actionError, notification, lastShownActionError]);

    useEffect(() => {
      if (actionMessage) {
        notification.success(actionMessage);
        setActionMessage(null);
      }
    }, [actionMessage, notification]);

    useEffect(() => {
      Sentry.setContext("dashboard", {
        isAuthenticated,
        vmCount: vms.length,
        selectedVmId: selectedVmForDetails?.id,
        userCurrency,
        barrackId,
        isMobile,
        isTablet,
        hasError: !!error,
        isLoading
      });
    }, [isAuthenticated, vms.length, selectedVmForDetails?.id, userCurrency, 
        barrackId, isMobile, isTablet, error, isLoading]);

    useEffect(() => {
      logInfo('Dashboard component mounted', {
        isAuthenticated,
        isMobile,
        isTablet,
        userEmail: userEmail ? 'has_email' : 'no_email',
        propBarrackId: propBarrackId ? 'has_barrack_id' : 'no_barrack_id'
      });
    }, []);
    
    const closeEventSource = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
            
            logInfo('Event source closed', {
              retries: eventSourceRetries.current
            });
        }
    }, []);
const fetchUserCurrency = useCallback(async () => {
    if (!isAuthenticated) return;
    
    const startTime = performance.now();
    
    try {
        logInfo('Fetching user currency preference');
        
        const profileResponse = await api.getUserProfile();
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (profileResponse.success && profileResponse.profile && profileResponse.profile.preferred_currency) {
            setUserCurrency(profileResponse.profile.preferred_currency);
            
            logPerformance('fetch_user_currency', duration, {
                success: true,
                currency: profileResponse.profile.preferred_currency
            });
            
            logInfo('User currency loaded successfully', {
                currency: profileResponse.profile.preferred_currency,
                duration: `${duration.toFixed(2)}ms`
            });
        } else {
            logPerformance('fetch_user_currency', duration, {
                success: false,
                fallbackCurrency: 'USD'
            });
        }
    } catch (err) {
        if (err.name === 'AbortError') {
            return;
        }
        
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            logError('Fetch user currency network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            // Silent fail - will use default USD
            return;
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            logError('Fetch user currency rate limited', {
                duration: `${duration.toFixed(2)}ms`
            }, err);
            // Silent fail - will use default USD
            return;
        }
        
        // Position 4: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            logError('Fetch user currency error - mapped code', {
                code: err.code,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            return;
        }
        
        // Position 5: Fallback
        logError('Failed to fetch user currency', {
            duration: `${duration.toFixed(2)}ms`,
            errorName: err.name
        }, err);
    }
}, [isAuthenticated, handleAuthError]);    const debouncedFetchVMs = useCallback((delay = 300) => {
        if (fetchVMsTimeoutRef.current) {
            clearTimeout(fetchVMsTimeoutRef.current);
        }
        
        fetchVMsTimeoutRef.current = setTimeout(() => {
            if (!isFetchingVMs.current && isAuthenticated) {
                logInfo('Debounced VM fetch triggered', { delay });
                fetchBarrackVMs();
            }
            fetchVMsTimeoutRef.current = null;
        }, delay);
    }, [isAuthenticated]);

const refreshVMs = useCallback(async () => {
    if (!isAuthenticated || isFetchingVMs.current) return;
    
    try {
        logInfo('Refreshing VMs data');
        await fetchBarrackVMs();
        logInfo('VMs data refreshed successfully');
    } catch (error) {
        if (error.name === 'AbortError') {
            return;
        }
        
        // Position 1: Authentication error
        if (error.status === 401) {
            handleAuthError();
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(error.code)) {
            setError(error.error);
            logError('Refresh VMs network error', { 
                code: error.code 
            }, error);
            return;
        }
        
        // Position 3: Rate limit
        if (error.status === 429 || error.code === 'RATE_LIMIT_EXCEEDED') {
            setError(error.error);
            return;
        }
        
        // Position 4: Mapped error code
        if (error.code && ERROR_MESSAGES[error.code]) {
            setError(ERROR_MESSAGES[error.code]);
            logError('Refresh VMs error - mapped code', {
                code: error.code
            }, error);
            return;
        }
        
        // Position 5: Fallback
        const errorMessage = error.error || getSafeErrorMessage(error);
        setError(errorMessage);
        
        logError('Failed to refresh VMs data', {
            errorName: error.name
        }, error);
    }
}, [isAuthenticated, handleAuthError]);const refreshCreditBalance = useCallback(async () => {
    if (!isAuthenticated || isFetchingBalance.current) return;
    
    try {
        logInfo('Refreshing credit balance');
        await fetchCreditBalance();
        logInfo('Credit balance refreshed successfully');
    } catch (error) {
        if (error.name === 'AbortError') {
            return;
        }
        
        // Position 1: Authentication error
        if (error.status === 401) {
            handleAuthError();
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(error.code)) {
            setError(error.error);
            logError('Refresh credit balance network error', { 
                code: error.code 
            }, error);
            return;
        }
        
        // Position 3: Rate limit
        if (error.status === 429 || error.code === 'RATE_LIMIT_EXCEEDED') {
            setError(error.error);
            return;
        }
        
        // Position 4: Mapped error code
        if (error.code && ERROR_MESSAGES[error.code]) {
            setError(ERROR_MESSAGES[error.code]);
            logError('Refresh credit balance error - mapped code', {
                code: error.code
            }, error);
            return;
        }
        
        // Position 5: Fallback
        const errorMessage = error.error || getSafeErrorMessage(error);
        setError(errorMessage);
        
        logError('Failed to refresh credit balance', {
            errorName: error.name
        }, error);
    }
}, [isAuthenticated, handleAuthError]);

    const clearError = useCallback(() => {
      setError(null);
      setLastShownError('');
    }, []);

    const clearActionError = useCallback(() => {
      setActionError(null);
      setLastShownActionError('');
    }, []);

    const handleStartClick = useCallback((vm) => {
        if (!vm) {
            setActionError('No VM selected');
            return;
        }
        
        if (!isAuthenticated) {
            logUserAction('start_redirect_signup', {
              vmId: vm.id,
              vmName: vm.name
            });
            navigate('/signup');
            return;
        }
        
        if (!canStartVM(vm)) {
            const statusMessages = {
                'ACTIVE': 'VM is already running',
                'RUNNING': 'VM is already running',
                'HIBERNATED': 'VM is hibernated. Restore the VM before starting.',
                'STARTING': 'VM start operation already in progress'
            };
            setActionError(statusMessages[vm.status] || `VM cannot be started in current state: ${vm.status}`);
            logUserAction('start_blocked_invalid_status', {
              vmId: vm.id,
              vmName: vm.name,
              vmStatus: vm.status
            });
            return;
        }
        
        logUserAction('start_vm_modal_opened', {
          vmId: vm.id,
          vmName: vm.name,
          vmStatus: vm.status
        });
        
        logInfo('VM start modal opened', {
          vmId: vm.id,
          vmName: vm.name
        });
        
        setSelectedVmForStart(vm);
    }, [isAuthenticated, navigate]);

    const handleStopClick = useCallback((vm) => {
        if (!vm) {
            setActionError('No VM selected');
            return;
        }
        
        if (!isAuthenticated) {
            logUserAction('stop_redirect_signup', {
              vmId: vm.id,
              vmName: vm.name
            });
            navigate('/signup');
            return;
        }
        
        if (!canStopVM(vm)) {
            const statusMessages = {
                'STOPPED': 'VM is already stopped',
                'SHUTOFF': 'VM is already stopped',
                'HIBERNATED': 'VM is hibernated. Restore the VM before stopping.',
                'STOPPING': 'VM stop operation already in progress',
                'STARTING': 'VM is starting. Wait for VM to start completely before stopping.'
            };
            setActionError(statusMessages[vm.status] || `VM cannot be stopped in current state: ${vm.status}`);
            logUserAction('stop_blocked_invalid_status', {
              vmId: vm.id,
              vmName: vm.name,
              vmStatus: vm.status
            });
            return;
        }
        
        logUserAction('stop_vm_modal_opened', {
          vmId: vm.id,
          vmName: vm.name,
          vmStatus: vm.status
        });
        
        logInfo('VM stop modal opened', {
          vmId: vm.id,
          vmName: vm.name
        });
        
        setSelectedVmForStop(vm);
    }, [isAuthenticated, navigate]);

    const handleRebootClick = useCallback((vm) => {
        if (!vm) {
            setActionError('No VM selected');
            return;
        }
        
        if (!isAuthenticated) {
            logUserAction('reboot_redirect_signup', {
              vmId: vm.id,
              vmName: vm.name
            });
            navigate('/signup');
            return;
        }
        
        if (!canRebootVM(vm)) {
            if (isAdvancedRegion(vm)) {
                setActionError('VM reboot is only available for legacy tier VMs');
                logUserAction('reboot_blocked_advanced_tier', {
                  vmId: vm.id,
                  vmName: vm.name,
                  tier: vm.tier
                });
                return;
            }
            
            const statusMessages = {
                'STOPPED': 'VM is stopped. Start the VM before rebooting.',
                'SHUTOFF': 'VM is stopped. Start the VM before rebooting.',
                'HIBERNATED': 'VM is hibernated. Restore the VM before rebooting.',
                'REBOOTING': 'VM reboot operation already in progress'
            };
            setActionError(statusMessages[vm.status] || `VM cannot be rebooted in current state: ${vm.status}`);
            logUserAction('reboot_blocked_invalid_status', {
              vmId: vm.id,
              vmName: vm.name,
              vmStatus: vm.status
            });
            return;
        }
        
        logUserAction('reboot_vm_modal_opened', {
          vmId: vm.id,
          vmName: vm.name,
          vmStatus: vm.status
        });
        
        logInfo('VM reboot modal opened', {
          vmId: vm.id,
          vmName: vm.name
        });
        
        setSelectedVmForReboot(vm);
    }, [isAuthenticated, navigate]);
const handleTakeSnapshotClick = useCallback(async () => {
    if (!selectedVmForDetails) {
        setActionError('No VM selected');
        return;
    }
    
    if (isAdvancedRegion(selectedVmForDetails)) {
        setActionError('Snapshots are only available for VMs in legacy regions');
        logUserAction('take_snapshot_blocked', {
            vmId: selectedVmForDetails.id,
            vmName: selectedVmForDetails.name,
            reason: 'advanced_region_not_supported'
        });
        return;
    }
    
    if (selectedVmForDetails.status !== 'RUNNING') {
        setActionError(`VM must be RUNNING to create snapshot (current: ${selectedVmForDetails.status})`);
        logUserAction('take_snapshot_blocked', {
            vmId: selectedVmForDetails.id,
            vmName: selectedVmForDetails.name,
            vmStatus: selectedVmForDetails.status,
            reason: 'vm_not_active'
        });
        return;
    }
        
    logUserAction('take_snapshot_modal_opened', {
        vmId: selectedVmForDetails.id,
        vmName: selectedVmForDetails.name,
        vmStatus: selectedVmForDetails.status
    });

    try {
        const snapshotsResponse = await api.getSnapshots();
        if (snapshotsResponse.success) {
            setExistingSnapshotNames((snapshotsResponse.snapshots || []).map(s => truncateText(s.name, 100)));
        }
    } catch (err) {
        if (err.name === 'AbortError') {
            return;
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            logError('Failed to fetch snapshots for duplicate checking - network error', { 
                code: err.code 
            }, err);
            // Continue to open modal - duplicate checking will be unavailable
        }
        
        // Position 3: Rate limit
        else if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            logError('Failed to fetch snapshots for duplicate checking - rate limited', {}, err);
            // Continue to open modal
        }
        
        // Position 4: Fallback
        else {
            logError('Failed to fetch snapshots for duplicate checking', {
                code: err.code,
                errorName: err.name
            }, err);
            // Continue to open modal
        }
    }
        
    setSelectedVmForSnapshot(selectedVmForDetails);
}, [selectedVmForDetails, handleAuthError]);const handleCreateVolumeClick = useCallback(async () => {
    logUserAction('create_volume_modal_opened', {
        source: 'dashboard',
        selectedVmId: selectedVmForDetails?.id
    });
    
    try {
        const [imagesResponse, volumesResponse] = await Promise.all([
            api.getImages('CANADA-1'),
            api.getVolumesWithNextName()
        ]);
            
        if (imagesResponse.success) {
            const fetchedImages = imagesResponse.images || [];
          const ubuntuImages = fetchedImages.filter(img => 
    (img.name && img.name.toLowerCase().includes('ubuntu')) ||
    (img.image_type && img.image_type.toLowerCase().includes('ubuntu'))
);
            setOsImages(ubuntuImages);
        }
        
        if (volumesResponse.success) {
            setExistingVolumeNames((volumesResponse.volumes || []).map(v => truncateText(v.name, 100)));
        }
    } catch (err) {
        if (err.name === 'AbortError') {
            return;
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            logError('Failed to fetch data for volume modal - network error', { 
                code: err.code 
            }, err);
            // Continue to open modal with defaults
        }
        
        // Position 3: Rate limit
        else if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            logError('Failed to fetch data for volume modal - rate limited', {}, err);
            // Continue to open modal with defaults
        }
        
        // Position 4: Fallback
        else {
            logError('Failed to fetch data for volume modal', {
                code: err.code,
                errorName: err.name
            }, err);
            // Continue to open modal with defaults
        }
    }
    
    setShowCreateVolumeModal(true);
}, [selectedVmForDetails, handleAuthError]);
    const handleHibernateClick = useCallback((vm) => {
        if (!vm) {
            setActionError('No VM selected');
            return;
        }
        
        if (!isAuthenticated) {
            logUserAction('hibernate_redirect_signup', {
              vmId: vm.id,
              vmName: vm.name
            });
            navigate('/signup');
            return;
        }
        
        if (!canHibernateVM(vm)) {
            const statusMessages = {
                'HIBERNATED': 'VM is already hibernated',
                'STARTING': 'VM is starting. Wait for VM to start completely before hibernating.',
                'STOPPING': 'VM is stopping. Wait for VM to stop completely before hibernating.',
                'HIBERNATING': 'VM hibernation operation already in progress'
            };
            setActionError(statusMessages[vm.status] || `VM cannot be hibernated in current state: ${vm.status}`);
            logUserAction('hibernate_blocked_invalid_status', {
              vmId: vm.id,
              vmName: vm.name,
              vmStatus: vm.status
            });
            return;
        }
        
        logUserAction('hibernate_vm_modal_opened', {
          vmId: vm.id,
          vmName: vm.name,
          vmStatus: vm.status
        });
        
        logInfo('VM hibernation modal opened', {
          vmId: vm.id,
          vmName: vm.name
        });
        
        setSelectedVmForHibernate(vm);
    }, [isAuthenticated, navigate]);

    const handleRestoreClick = useCallback((vm) => {
        if (!vm) {
            setActionError('No VM selected');
            return;
        }
        
        if (!isAuthenticated) {
            logUserAction('restore_redirect_signup', {
              vmId: vm.id,
              vmName: vm.name
            });
            navigate('/signup');
            return;
        }
        
        if (!canRestoreVM(vm)) {
            setActionError(`VM cannot be restored. VM must be HIBERNATED to restore (current: ${vm.status})`);
            logUserAction('restore_blocked_invalid_status', {
              vmId: vm.id,
              vmName: vm.name,
              vmStatus: vm.status
            });
            return;
        }
        
        logUserAction('restore_vm_modal_opened', {
          vmId: vm.id,
          vmName: vm.name,
          vmStatus: vm.status
        });
        
        setSelectedVmForRestore(vm);
    }, [isAuthenticated, navigate]);
const handleStartVM = useCallback(async () => {
    if (!selectedVmForStart) {
        setActionError('No VM selected for start operation');
        setSelectedVmForStart(null);
        return;
    }
    
    const vmId = selectedVmForStart.instance_id;
    
    if (!vmId) {
        setActionError('Unable to identify VM for start operation');
        setSelectedVmForStart(null);
        return;
    }
    
    if (operationRefs.current.starting.has(vmId) || 
        loadingStates.starting.has(vmId) || 
        !isAuthenticated) {
        return;
    }
    
    const startTime = performance.now();
    
    operationRefs.current.starting.add(vmId);
    
    setLoadingStates(prev => ({ 
        ...prev, 
        starting: new Set([...prev.starting, vmId])
    }));
    setActionError(null);
    setActionMessage(null);
        
    logUserAction('vm_start_confirmed', {
      vmId: vmId,
      vmName: selectedVmForStart.name
    });
    
    logInfo('Starting VM start operation', {
      vmId: vmId,
      vmName: selectedVmForStart.name
    });
    
    try {
        setVMs(prevVMs => 
            prevVMs.map(v => 
                v.instance_id === vmId
                    ? {...v, status: 'STARTING'}
                    : v
            )
        );
        
        const response = await api.startVM(vmId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setActionMessage(`VM ${sanitizeText(selectedVmForStart.name)} is being started`);
            
            setTimeout(() => {
                fetchBarrackVMs();
            }, 500);
            
            setLockedVMs(prev => new Set([...prev, vmId]));
            setTimeout(() => {
                setLockedVMs(prev => {
                    const newLocked = new Set(prev);
                    newLocked.delete(vmId);
                    return newLocked;
                });
            }, 3000);
            
            logPerformance('start_vm', duration, {
              success: true,
              vmId: vmId
            });
            
            logInfo('VM start initiated successfully', {
              vmId: vmId,
              vmName: selectedVmForStart.name,
              duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vm_start_success', {
              vmId: vmId,
              vmName: selectedVmForStart.name,
              duration: `${duration.toFixed(2)}ms`
            });
        } else {
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForStart.status}
                        : v
                )
            );
            
            const errorMessage = getSafeErrorMessage(response);
            setActionError(errorMessage);
            
            logError('VM start failed', {
              vmId: vmId,
              vmName: selectedVmForStart.name,
              duration: `${duration.toFixed(2)}ms`,
              error: errorMessage
            });
        }
    } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
            operationRefs.current.starting.delete(vmId);
            setLoadingStates(prev => {
                const newStarting = new Set(prev.starting);
                newStarting.delete(vmId);
                return { ...prev, starting: newStarting };
            });
            setSelectedVmForStart(null);
            return;
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            operationRefs.current.starting.delete(vmId);
            setLoadingStates(prev => {
                const newStarting = new Set(prev.starting);
                newStarting.delete(vmId);
                return { ...prev, starting: newStarting };
            });
            setSelectedVmForStart(null);
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            setActionError(err.error);
            logError('VM start network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`,
                vmId: vmId
            }, err);
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForStart.status}
                        : v
                )
            );
            operationRefs.current.starting.delete(vmId);
            setLoadingStates(prev => {
                const newStarting = new Set(prev.starting);
                newStarting.delete(vmId);
                return { ...prev, starting: newStarting };
            });
            setSelectedVmForStart(null);
            return;
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            setActionError(err.error);
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForStart.status}
                        : v
                )
            );
            operationRefs.current.starting.delete(vmId);
            setLoadingStates(prev => {
                const newStarting = new Set(prev.starting);
                newStarting.delete(vmId);
                return { ...prev, starting: newStarting };
            });
            setSelectedVmForStart(null);
            return;
        }
        
        // Position 4: VM not found
        if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
            notification.error('This VM no longer exists');
            setVMs(prevVMs => prevVMs.filter(v => v.instance_id !== vmId));
            setSelectedVmForDetails(null);
            operationRefs.current.starting.delete(vmId);
            setLoadingStates(prev => {
                const newStarting = new Set(prev.starting);
                newStarting.delete(vmId);
                return { ...prev, starting: newStarting };
            });
            setSelectedVmForStart(null);
            return;
        }
        
        // Position 5: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            setActionError(ERROR_MESSAGES[err.code]);
            logError('VM start error - mapped code', {
                code: err.code,
                vmId: vmId,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForStart.status}
                        : v
                )
            );
            operationRefs.current.starting.delete(vmId);
            setLoadingStates(prev => {
                const newStarting = new Set(prev.starting);
                newStarting.delete(vmId);
                return { ...prev, starting: newStarting };
            });
            setSelectedVmForStart(null);
            return;
        }
        
        // Position 6: Fallback
        const errorMessage = err.error || getSafeErrorMessage(err);
        setActionError(errorMessage);
        
        logError('VM start error', {
          vmId: vmId,
          vmName: selectedVmForStart.name,
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name
        }, err);
        
        logUserAction('vm_start_failed', {
          vmId: vmId,
          vmName: selectedVmForStart.name,
          error: errorMessage
        });
        
        setVMs(prevVMs => 
            prevVMs.map(v => 
                v.instance_id === vmId
                    ? {...v, status: selectedVmForStart.status}
                    : v
            )
        );
    } finally {
        operationRefs.current.starting.delete(vmId);
        setLoadingStates(prev => {
            const newStarting = new Set(prev.starting);
            newStarting.delete(vmId);
            return { ...prev, starting: newStarting };
        });
        setSelectedVmForStart(null);
    }
}, [selectedVmForStart, vms, loadingStates.starting, isAuthenticated, handleAuthError, notification]);

const handleStopVM = useCallback(async () => {
    if (!selectedVmForStop) {
        setActionError('No VM selected for stop operation');
        setSelectedVmForStop(null);
        return;
    }
    
    const vmId = selectedVmForStop.instance_id;
    
    if (!vmId) {
        setActionError('Unable to identify VM for stop operation');
        setSelectedVmForStop(null);
        return;
    }
    
    const latestVm = vms.find(v => v.instance_id === vmId);
    
    if (!latestVm) {
        setActionError('VM not found. It may have been deleted.');
        setSelectedVmForStop(null);
        return;
    }
    
    if (!canStopVM(latestVm)) {
        const statusMessages = {
            'STOPPED': 'VM is already stopped',
            'SHUTOFF': 'VM is already stopped',
            'STOPPING': 'VM stop operation already in progress',
            'HIBERNATED': 'VM is hibernated. Restore the VM before stopping.',
            'STARTING': 'VM is starting. Wait for VM to start completely before stopping.'
        };
        setActionError(statusMessages[latestVm.status] || `VM cannot be stopped in current state: ${latestVm.status}`);
        setSelectedVmForStop(null);
        return;
    }
    
    if (operationRefs.current.stopping.has(vmId) || 
        loadingStates.stopping.has(vmId) || 
        !isAuthenticated) {
        return;
    }
    
    const startTime = performance.now();
    
    operationRefs.current.stopping.add(vmId);
    
    setLoadingStates(prev => ({ 
        ...prev, 
        stopping: new Set([...prev.stopping, vmId])
    }));
    setActionError(null);
    setActionMessage(null);
    
    logUserAction('vm_stop_confirmed', {
      vmId: vmId,
      vmName: selectedVmForStop.name
    });
    
    logInfo('Starting VM stop operation', {
      vmId: vmId,
      vmName: selectedVmForStop.name
    });
    
    try {
        setVMs(prevVMs => 
            prevVMs.map(v => 
                v.instance_id === vmId
                    ? {...v, status: 'STOPPING'}
                    : v
            )
        );
        
        const response = await api.stopVM(vmId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setActionMessage(`VM ${sanitizeText(selectedVmForStop.name)} is being stopped`);
            
            setTimeout(() => {
                fetchBarrackVMs();
            }, 500);
            
            setLockedVMs(prev => new Set([...prev, vmId]));
            setTimeout(() => {
                setLockedVMs(prev => {
                    const newLocked = new Set(prev);
                    newLocked.delete(vmId);
                    return newLocked;
                });
            }, 3000);
            
            logPerformance('stop_vm', duration, {
              success: true,
              vmId: vmId
            });
            
            logInfo('VM stop initiated successfully', {
              vmId: vmId,
              vmName: selectedVmForStop.name,
              duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vm_stop_success', {
              vmId: vmId,
              vmName: selectedVmForStop.name,
              duration: `${duration.toFixed(2)}ms`
            });
        } else {
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForStop.status}
                        : v
                )
            );
            
            const errorMessage = getSafeErrorMessage(response);
            setActionError(errorMessage);
            
            logError('VM stop failed', {
              vmId: vmId,
              vmName: selectedVmForStop.name,
              duration: `${duration.toFixed(2)}ms`,
              error: errorMessage
            });
        }
    } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
            operationRefs.current.stopping.delete(vmId);
            setLoadingStates(prev => {
                const newStopping = new Set(prev.stopping);
                newStopping.delete(vmId);
                return { ...prev, stopping: newStopping };
            });
            setSelectedVmForStop(null);
            return;
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            operationRefs.current.stopping.delete(vmId);
            setLoadingStates(prev => {
                const newStopping = new Set(prev.stopping);
                newStopping.delete(vmId);
                return { ...prev, stopping: newStopping };
            });
            setSelectedVmForStop(null);
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            setActionError(err.error);
            logError('VM stop network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`,
                vmId: vmId
            }, err);
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForStop.status}
                        : v
                )
            );
            operationRefs.current.stopping.delete(vmId);
            setLoadingStates(prev => {
                const newStopping = new Set(prev.stopping);
                newStopping.delete(vmId);
                return { ...prev, stopping: newStopping };
            });
            setSelectedVmForStop(null);
            return;
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            setActionError(err.error);
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForStop.status}
                        : v
                )
            );
            operationRefs.current.stopping.delete(vmId);
            setLoadingStates(prev => {
                const newStopping = new Set(prev.stopping);
                newStopping.delete(vmId);
                return { ...prev, stopping: newStopping };
            });
            setSelectedVmForStop(null);
            return;
        }
        
        // Position 4: VM not found
        if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
            notification.error('This VM no longer exists');
            setVMs(prevVMs => prevVMs.filter(v => v.instance_id !== vmId));
            setSelectedVmForDetails(null);
            operationRefs.current.stopping.delete(vmId);
            setLoadingStates(prev => {
                const newStopping = new Set(prev.stopping);
                newStopping.delete(vmId);
                return { ...prev, stopping: newStopping };
            });
            setSelectedVmForStop(null);
            return;
        }
        
        // Position 5: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            setActionError(ERROR_MESSAGES[err.code]);
            logError('VM stop error - mapped code', {
                code: err.code,
                vmId: vmId,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForStop.status}
                        : v
                )
            );
            operationRefs.current.stopping.delete(vmId);
            setLoadingStates(prev => {
                const newStopping = new Set(prev.stopping);
                newStopping.delete(vmId);
                return { ...prev, stopping: newStopping };
            });
            setSelectedVmForStop(null);
            return;
        }
        
        // Position 6: Fallback
        const errorMessage = err.error || getSafeErrorMessage(err);
        setActionError(errorMessage);
        
        logError('VM stop error', {
          vmId: vmId,
          vmName: selectedVmForStop.name,
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name
        }, err);
        
        logUserAction('vm_stop_failed', {
          vmId: vmId,
          vmName: selectedVmForStop.name,
          error: errorMessage
        });
        
        setVMs(prevVMs => 
            prevVMs.map(v => 
                v.instance_id === vmId
                    ? {...v, status: selectedVmForStop.status}
                    : v
            )
        );
    } finally {
        operationRefs.current.stopping.delete(vmId);
        setLoadingStates(prev => {
            const newStopping = new Set(prev.stopping);
            newStopping.delete(vmId);
            return { ...prev, stopping: newStopping };
        });
        setSelectedVmForStop(null);
    }
}, [selectedVmForStop, vms, loadingStates.stopping, isAuthenticated, handleAuthError, notification]);

const handleRebootVM = useCallback(async () => {
    if (!selectedVmForReboot) {
        setActionError('No VM selected for reboot operation');
        setSelectedVmForReboot(null);
        return;
    }
    
    const vmId = selectedVmForReboot.instance_id;
    
    if (!vmId) {
        setActionError('Unable to identify VM for reboot operation');
        setSelectedVmForReboot(null);
        return;
    }
    
    const latestVm = vms.find(v => v.instance_id === vmId);
    
    if (!latestVm) {
        setActionError('VM not found. It may have been deleted.');
        setSelectedVmForReboot(null);
        return;
    }
    
    if (!canRebootVM(latestVm)) {
        if (isAdvancedRegion(latestVm)) {
            setActionError('VM reboot is only available for legacy tier VMs');
            setSelectedVmForReboot(null);
            return;
        }
        
        const statusMessages = {
            'STOPPED': 'VM is stopped. Start the VM before rebooting.',
            'SHUTOFF': 'VM is stopped. Start the VM before rebooting.',
            'REBOOTING': 'VM reboot operation already in progress',
            'HIBERNATED': 'VM is hibernated. Restore the VM before rebooting.'
        };
        setActionError(statusMessages[latestVm.status] || `VM cannot be rebooted in current state: ${latestVm.status}`);
        setSelectedVmForReboot(null);
        return;
    }
    
    if (operationRefs.current.rebooting.has(vmId) || 
        loadingStates.rebooting.has(vmId) || 
        !isAuthenticated) {
        return;
    }
    
    const startTime = performance.now();
    
    operationRefs.current.rebooting.add(vmId);
    
    setLoadingStates(prev => ({ 
        ...prev, 
        rebooting: new Set([...prev.rebooting, vmId])
    }));
    setActionError(null);
    setActionMessage(null);
        
    logUserAction('vm_reboot_confirmed', {
      vmId: vmId,
      vmName: selectedVmForReboot.name
    });
    
    logInfo('Starting VM reboot operation', {
      vmId: vmId,
      vmName: selectedVmForReboot.name
    });
    
    try {
        setVMs(prevVMs => 
            prevVMs.map(v => 
                v.instance_id === vmId
                    ? {...v, status: 'REBOOTING'}
                    : v
            )
        );
        
        const response = await api.rebootVM(vmId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setActionMessage(`VM ${sanitizeText(selectedVmForReboot.name)} is being rebooted`);
            
            setTimeout(() => {
                fetchBarrackVMs();
            }, 500);
            
            setLockedVMs(prev => new Set([...prev, vmId]));
            setTimeout(() => {
                setLockedVMs(prev => {
                    const newLocked = new Set(prev);
                    newLocked.delete(vmId);
                    return newLocked;
                });
            }, 3000);
            
            logPerformance('reboot_vm', duration, {
              success: true,
              vmId: vmId
            });
            
            logInfo('VM reboot initiated successfully', {
              vmId: vmId,
              vmName: selectedVmForReboot.name,
              duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vm_reboot_success', {
              vmId: vmId,
              vmName: selectedVmForReboot.name,
              duration: `${duration.toFixed(2)}ms`
            });
        } else {
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForReboot.status}
                        : v
                )
            );
            
            const errorMessage = getSafeErrorMessage(response);
            setActionError(errorMessage);
            
            logError('VM reboot failed', {
              vmId: vmId,
              vmName: selectedVmForReboot.name,
              duration: `${duration.toFixed(2)}ms`,
              error: errorMessage
            });
        }
    } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
            operationRefs.current.rebooting.delete(vmId);
            setLoadingStates(prev => {
                const newRebooting = new Set(prev.rebooting);
                newRebooting.delete(vmId);
                return { ...prev, rebooting: newRebooting };
            });
            setSelectedVmForReboot(null);
            return;
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            operationRefs.current.rebooting.delete(vmId);
            setLoadingStates(prev => {
                const newRebooting = new Set(prev.rebooting);
                newRebooting.delete(vmId);
                return { ...prev, rebooting: newRebooting };
            });
            setSelectedVmForReboot(null);
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            setActionError(err.error);
            logError('VM reboot network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`,
                vmId: vmId
            }, err);
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForReboot.status}
                        : v
                )
            );
            operationRefs.current.rebooting.delete(vmId);
            setLoadingStates(prev => {
                const newRebooting = new Set(prev.rebooting);
                newRebooting.delete(vmId);
                return { ...prev, rebooting: newRebooting };
            });
            setSelectedVmForReboot(null);
            return;
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            setActionError(err.error);
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForReboot.status}
                        : v
                )
            );
            operationRefs.current.rebooting.delete(vmId);
            setLoadingStates(prev => {
                const newRebooting = new Set(prev.rebooting);
                newRebooting.delete(vmId);
                return { ...prev, rebooting: newRebooting };
            });
            setSelectedVmForReboot(null);
            return;
        }
        
        // Position 4: VM not found
        if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
            notification.error('This VM no longer exists');
            setVMs(prevVMs => prevVMs.filter(v => v.instance_id !== vmId));
            setSelectedVmForDetails(null);
            operationRefs.current.rebooting.delete(vmId);
            setLoadingStates(prev => {
                const newRebooting = new Set(prev.rebooting);
                newRebooting.delete(vmId);
                return { ...prev, rebooting: newRebooting };
            });
            setSelectedVmForReboot(null);
            return;
        }
        
        // Position 5: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            setActionError(ERROR_MESSAGES[err.code]);
            logError('VM reboot error - mapped code', {
                code: err.code,
                vmId: vmId,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            setVMs(prevVMs => 
                prevVMs.map(v => 
                    v.instance_id === vmId
                        ? {...v, status: selectedVmForReboot.status}
                        : v
                )
            );
            operationRefs.current.rebooting.delete(vmId);
            setLoadingStates(prev => {
                const newRebooting = new Set(prev.rebooting);
                newRebooting.delete(vmId);
                return { ...prev, rebooting: newRebooting };
            });
            setSelectedVmForReboot(null);
            return;
        }
        
        // Position 6: Fallback
        const errorMessage = err.error || getSafeErrorMessage(err);
        setActionError(errorMessage);
        
        logError('VM reboot error', {
          vmId: vmId,
          vmName: selectedVmForReboot.name,
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name
        }, err);
        
        logUserAction('vm_reboot_failed', {
          vmId: vmId,
          vmName: selectedVmForReboot.name,
          error: errorMessage
        });
        
        setVMs(prevVMs => 
            prevVMs.map(v => 
                v.instance_id === vmId
                    ? {...v, status: selectedVmForReboot.status}
                    : v
            )
        );
    } finally {
        operationRefs.current.rebooting.delete(vmId);
        setLoadingStates(prev => {
            const newRebooting = new Set(prev.rebooting);
            newRebooting.delete(vmId);
            return { ...prev, rebooting: newRebooting };
        });
        setSelectedVmForReboot(null);
    }
}, [selectedVmForReboot, vms, loadingStates.rebooting, isAuthenticated, handleAuthError, notification]);

const handleDeleteSelectedVM = useCallback(async () => {
    if (!selectedVmForDetails) {
        setActionError('No VM selected for deletion');
        setShowDeleteModal(false);
        return;
    }
    
    const vmId = selectedVmForDetails.instance_id;
    
    if (!vmId) {
        setActionError('Unable to identify VM for deletion');
        setShowDeleteModal(false);
        return;
    }
    
    if (operationRefs.current.deleting.has(vmId) || 
        loadingStates.deleting.has(vmId) || 
        !isAuthenticated) {
        return;
    }
    
    const startTime = performance.now();
    
    operationRefs.current.deleting.add(vmId);
    
    setLoadingStates(prev => ({ 
        ...prev, 
        deleting: new Set([...prev.deleting, vmId])
    }));
    setShowDeleteModal(false);
    setShowDeleteNotification(true);
    
    logUserAction('vm_delete_confirmed', {
      vmId: vmId,
      vmName: selectedVmForDetails.name
    });
    
    logInfo('Starting VM deletion from dashboard', {
      vmId: vmId,
      vmName: selectedVmForDetails.name
    });
    
    try {
        const response = await api.deleteVM(vmId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setVMs(vms.filter(vm => vm.instance_id !== vmId));
            setSelectedVmForDetails(null);
            
            setTimeout(() => {
                fetchBarrackVMs();
            }, 500);
            
            logPerformance('delete_vm_dashboard', duration, {
              success: true,
              vmId: vmId
            });
            
            logInfo('VM deleted successfully from dashboard', {
              vmId: vmId,
              vmName: selectedVmForDetails.name,
              duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vm_delete_success_dashboard', {
              vmId: vmId,
              vmName: selectedVmForDetails.name,
              duration: `${duration.toFixed(2)}ms`
            });
        } else {
            const errorMessage = getSafeErrorMessage(response);
            setActionError(errorMessage);
            
            logError('VM deletion failed from dashboard', {
              vmId: vmId,
              vmName: selectedVmForDetails.name,
              duration: `${duration.toFixed(2)}ms`,
              error: errorMessage
            });
            
            logUserAction('vm_delete_failed_dashboard', {
              vmId: vmId,
              vmName: selectedVmForDetails.name,
              error: errorMessage
            });
            
            throw new Error(errorMessage);
        }
    } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
            operationRefs.current.deleting.delete(vmId);
            setLoadingStates(prev => {
                const newDeleting = new Set(prev.deleting);
                newDeleting.delete(vmId);
                return { ...prev, deleting: newDeleting };
            });
            setShowDeleteNotification(false);
            return;
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            operationRefs.current.deleting.delete(vmId);
            setLoadingStates(prev => {
                const newDeleting = new Set(prev.deleting);
                newDeleting.delete(vmId);
                return { ...prev, deleting: newDeleting };
            });
            setShowDeleteNotification(false);
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            setActionError(err.error);
            logError('VM deletion network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`,
                vmId: vmId
            }, err);
            operationRefs.current.deleting.delete(vmId);
            setLoadingStates(prev => {
                const newDeleting = new Set(prev.deleting);
                newDeleting.delete(vmId);
                return { ...prev, deleting: newDeleting };
            });
            setShowDeleteNotification(false);
            return;
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            setActionError(err.error);
            operationRefs.current.deleting.delete(vmId);
            setLoadingStates(prev => {
                const newDeleting = new Set(prev.deleting);
                newDeleting.delete(vmId);
                return { ...prev, deleting: newDeleting };
            });
            setShowDeleteNotification(false);
            return;
        }
        
        // Position 4: VM not found
        if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
            notification.error('This VM no longer exists');
            setVMs(prevVMs => prevVMs.filter(v => v.instance_id !== vmId));
            setSelectedVmForDetails(null);
            operationRefs.current.deleting.delete(vmId);
            setLoadingStates(prev => {
                const newDeleting = new Set(prev.deleting);
                newDeleting.delete(vmId);
                return { ...prev, deleting: newDeleting };
            });
            setShowDeleteNotification(false);
            return;
        }
        
        // Position 5: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            setActionError(ERROR_MESSAGES[err.code]);
            logError('VM deletion error - mapped code', {
                code: err.code,
                vmId: vmId,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            operationRefs.current.deleting.delete(vmId);
            setLoadingStates(prev => {
                const newDeleting = new Set(prev.deleting);
                newDeleting.delete(vmId);
                return { ...prev, deleting: newDeleting };
            });
            setShowDeleteNotification(false);
            return;
        }
        
        // Position 6: Fallback
        const errorMessage = err.error || getSafeErrorMessage(err);
        setActionError(errorMessage);
        
        logError('VM deletion error from dashboard', {
          vmId: vmId,
          vmName: selectedVmForDetails.name,
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name
        }, err);
    } finally {
        operationRefs.current.deleting.delete(vmId);
        setLoadingStates(prev => {
            const newDeleting = new Set(prev.deleting);
            newDeleting.delete(vmId);
            return { ...prev, deleting: newDeleting };
        });
        setShowDeleteNotification(false);
    }
}, [selectedVmForDetails, vms, loadingStates.deleting, isAuthenticated, handleAuthError, notification]);

const handleSnapshotConfirm = useCallback(async (snapshotDataFromModal) => {
    if (!selectedVmForSnapshot || selectedVmForSnapshot.status !== 'RUNNING') {
        logError('Cannot create snapshot - VM not running', {
            vmId: selectedVmForSnapshot?.instance_id,
            vmStatus: selectedVmForSnapshot?.status
        });
        return;
    }
    
    const vmId = selectedVmForSnapshot.instance_id;
    
    if (!vmId) {
        setActionError('Unable to identify VM for snapshot');
        setSelectedVmForSnapshot(null);
        return;
    }

    if (isAdvancedRegion(selectedVmForSnapshot)) {
        logError('Cannot create snapshot - Advanced region not supported', {
            vmId: vmId,
            region: selectedVmForSnapshot.region
        });
        return;
    }
    
    if (operationRefs.current.creatingSnapshot.has(vmId) ||
        loadingStates.creatingSnapshot.has(vmId)) {
        return;
    }
        
    const startTime = performance.now();
    
    operationRefs.current.creatingSnapshot.add(vmId);
    
    setLoadingStates(prev => ({ 
        ...prev, 
        creatingSnapshot: new Set([...prev.creatingSnapshot, vmId])
    }));
    
    logUserAction('snapshot_creation_confirmed', {
        vmId: vmId,
        vmName: selectedVmForSnapshot.name,
        snapshotName: snapshotDataFromModal.name,
        hasDescription: !!snapshotDataFromModal.description
    });
    
    try {
        const snapshotData = {
            name: truncateText(sanitizeText(snapshotDataFromModal.name), 100),
            description: snapshotDataFromModal.description 
                ? truncateText(sanitizeText(snapshotDataFromModal.description), 500)
                : `Snapshot of ${selectedVmForSnapshot.name} created on ${new Date().toISOString().slice(0, 10)}`
        };
        
        const response = await api.createSnapshot(vmId, snapshotData);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setActionMessage(`Snapshot ${snapshotData.name} is being created for VM ${selectedVmForSnapshot.name}`);
            
            setTimeout(() => {
                fetchBarrackVMs();
            }, 500);
            
            logPerformance('create_snapshot', duration, {
                vmId: vmId,
                snapshotName: snapshotData.name,
                success: true
            });
            
            logUserAction('snapshot_creation_success', {
                vmId: vmId,
                vmName: selectedVmForSnapshot.name,
                snapshotName: snapshotData.name,
                snapshotId: response.snapshot_id
            });
        } else {
            const errorMessage = getSafeErrorMessage(response);
            setActionError(errorMessage);
            
            logError('Snapshot creation failed', {
                vmId: vmId,
                snapshotName: snapshotData.name,
                error: errorMessage,
                duration: `${duration.toFixed(2)}ms`
            });
        }
    } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
            operationRefs.current.creatingSnapshot.delete(vmId);
            setLoadingStates(prev => {
                const newCreatingSnapshot = new Set(prev.creatingSnapshot);
                newCreatingSnapshot.delete(vmId);
                return { ...prev, creatingSnapshot: newCreatingSnapshot };
            });
            setSelectedVmForSnapshot(null);
            return;
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            operationRefs.current.creatingSnapshot.delete(vmId);
            setLoadingStates(prev => {
                const newCreatingSnapshot = new Set(prev.creatingSnapshot);
                newCreatingSnapshot.delete(vmId);
                return { ...prev, creatingSnapshot: newCreatingSnapshot };
            });
            setSelectedVmForSnapshot(null);
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            setActionError(err.error);
            logError('Snapshot creation network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`,
                vmId: vmId
            }, err);
            operationRefs.current.creatingSnapshot.delete(vmId);
            setLoadingStates(prev => {
                const newCreatingSnapshot = new Set(prev.creatingSnapshot);
                newCreatingSnapshot.delete(vmId);
                return { ...prev, creatingSnapshot: newCreatingSnapshot };
            });
            setSelectedVmForSnapshot(null);
            return;
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            setActionError(err.error);
            operationRefs.current.creatingSnapshot.delete(vmId);
            setLoadingStates(prev => {
                const newCreatingSnapshot = new Set(prev.creatingSnapshot);
                newCreatingSnapshot.delete(vmId);
                return { ...prev, creatingSnapshot: newCreatingSnapshot };
            });
            setSelectedVmForSnapshot(null);
            return;
        }
        
        // Position 4: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            setActionError(ERROR_MESSAGES[err.code]);
            logError('Snapshot creation error - mapped code', {
                code: err.code,
                vmId: vmId,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            operationRefs.current.creatingSnapshot.delete(vmId);
            setLoadingStates(prev => {
                const newCreatingSnapshot = new Set(prev.creatingSnapshot);
                newCreatingSnapshot.delete(vmId);
                return { ...prev, creatingSnapshot: newCreatingSnapshot };
            });
            setSelectedVmForSnapshot(null);
            return;
        }
        
        // Position 5: Fallback
        const errorMessage = err.error || getSafeErrorMessage(err);
        setActionError(errorMessage);
        
        logError('Snapshot creation error', {
            vmId: vmId,
            snapshotName: snapshotDataFromModal.name,
            errorName: err.name,
            duration: `${duration.toFixed(2)}ms`
        }, err);
    } finally {
        operationRefs.current.creatingSnapshot.delete(vmId);
        setLoadingStates(prev => {
            const newCreatingSnapshot = new Set(prev.creatingSnapshot);
            newCreatingSnapshot.delete(vmId);
            return { ...prev, creatingSnapshot: newCreatingSnapshot };
        });
        setSelectedVmForSnapshot(null);
    }
}, [selectedVmForSnapshot, loadingStates.creatingSnapshot, handleAuthError]);

const handleVolumeCreate = useCallback(async (volumeData) => {
    const startTime = performance.now();
    
    setLoadingStates(prev => ({ ...prev, creating: true }));
    
    logUserAction('volume_creation_initiated', {
        volumeName: volumeData.name,
        size: volumeData.size,
        region: volumeData.region,
        source: 'dashboard'
    });
    
    try {
        const response = await api.createVolume(volumeData);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setShowCreateVolumeModal(false);
            setActionMessage(`Volume ${truncateText(volumeData.name, 50)} is being created`);
            
            logPerformance('create_volume', duration, {
                volumeName: volumeData.name,
                size: volumeData.size,
                success: true
            });
            
            logUserAction('volume_creation_success', {
                volumeName: volumeData.name,
                volumeId: response.volume_id,
                source: 'dashboard'
            });
        } else {
            const errorMessage = getSafeErrorMessage(response);
            setActionError(errorMessage);
            
            logError('Volume creation failed', {
                volumeName: volumeData.name,
                error: errorMessage,
                duration: `${duration.toFixed(2)}ms`
            });
        }
    } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
            setLoadingStates(prev => ({ ...prev, creating: false }));
            return;
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            setLoadingStates(prev => ({ ...prev, creating: false }));
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            setActionError(err.error);
            logError('Volume creation network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`,
                volumeName: volumeData.name
            }, err);
            setLoadingStates(prev => ({ ...prev, creating: false }));
            return;
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            setActionError(err.error);
            setLoadingStates(prev => ({ ...prev, creating: false }));
            return;
        }
        
        // Position 4: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            setActionError(ERROR_MESSAGES[err.code]);
            logError('Volume creation error - mapped code', {
                code: err.code,
                volumeName: volumeData.name,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            setLoadingStates(prev => ({ ...prev, creating: false }));
            return;
        }
        
        // Position 5: Fallback
        const errorMessage = err.error || getSafeErrorMessage(err);
        setActionError(errorMessage);
        
        logError('Volume creation error', {
            volumeName: volumeData.name,
            errorName: err.name,
            duration: `${duration.toFixed(2)}ms`
        }, err);
    } finally {
        setLoadingStates(prev => ({ ...prev, creating: false }));
    }
}, [handleAuthError]);


const handleHibernateVM = useCallback(async () => {
    if (!selectedVmForHibernate) {
        setActionError('No VM selected for hibernation');
        setSelectedVmForHibernate(null);
        return;
    }
    
    const vmId = selectedVmForHibernate.instance_id;
    
    if (!vmId) {
        setActionError('Unable to identify VM for hibernation');
        setSelectedVmForHibernate(null);
        return;
    }
    
    const latestVm = vms.find(v => v.instance_id === vmId);
    
    if (!latestVm) {
        setActionError('VM not found. It may have been deleted.');
        setSelectedVmForHibernate(null);
        return;
    }
    
    if (!canHibernateVM(latestVm)) {
        const statusMessages = {
            'HIBERNATED': 'VM is already hibernated',
            'STARTING': 'VM is starting. Wait for VM to start completely before hibernating.',
            'STOPPING': 'VM is stopping. Wait for VM to stop completely before hibernating.',
            'HIBERNATING': 'VM hibernation operation already in progress'
        };
        setActionError(statusMessages[latestVm.status] || `VM cannot be hibernated in current state: ${latestVm.status}`);
        setSelectedVmForHibernate(null);
        return;
    }
    
    if (operationRefs.current.hibernating.has(vmId) || 
        loadingStates.hibernating.has(vmId) || 
        !isAuthenticated) {
        return;
    }
    
    const startTime = performance.now();
    
    operationRefs.current.hibernating.add(vmId);
    
    setLoadingStates(prev => ({ 
        ...prev, 
        hibernating: new Set([...prev.hibernating, vmId])
    }));
    setActionError(null);
    setActionMessage(null);
        
    logUserAction('vm_hibernate_confirmed', {
      vmId: vmId,
      vmName: selectedVmForHibernate.name
    });
    
    logInfo('Starting VM hibernation', {
      vmId: vmId,
      vmName: selectedVmForHibernate.name
    });
    
    try {
        setVMs(prevVMs => 
            prevVMs.map(vm => 
                vm.instance_id === vmId
                    ? {...vm, status: 'HIBERNATING'}
                    : vm
            )
        );
        
        const response = await api.hibernateVM(vmId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setActionMessage(`VM ${sanitizeText(selectedVmForHibernate.name)} is being hibernated`);
            
            setTimeout(() => {
                fetchBarrackVMs();
            }, 500);
            
            setLockedVMs(prev => new Set([...prev, vmId]));
            setTimeout(() => {
                setLockedVMs(prev => {
                    const newLocked = new Set(prev);
                    newLocked.delete(vmId);
                    return newLocked;
                });
            }, 3000);
            
            logPerformance('hibernate_vm', duration, {
              success: true,
              vmId: vmId
            });
            
            logInfo('VM hibernation initiated successfully', {
              vmId: vmId,
              vmName: selectedVmForHibernate.name,
              duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vm_hibernate_success', {
              vmId: vmId,
              vmName: selectedVmForHibernate.name,
              duration: `${duration.toFixed(2)}ms`
            });
        } else {
            setVMs(prevVMs => 
                prevVMs.map(vm => 
                    vm.instance_id === vmId
                        ? {...vm, status: selectedVmForHibernate.status}
                        : vm
                )
            );
            
            const errorMessage = getSafeErrorMessage(response);
            setActionError(errorMessage);
            
            logPerformance('hibernate_vm', duration, {
              success: false,
              error: errorMessage
            });
            
            logError('VM hibernation failed', {
              vmId: vmId,
              vmName: selectedVmForHibernate.name,
              duration: `${duration.toFixed(2)}ms`,
              error: errorMessage
            });
            
            logUserAction('vm_hibernate_failed', {
              vmId: vmId,
              vmName: selectedVmForHibernate.name,
              error: errorMessage
            });
            
            throw new Error(errorMessage);
        }
    } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
            operationRefs.current.hibernating.delete(vmId);
            setLoadingStates(prev => {
                const newHibernating = new Set(prev.hibernating);
                newHibernating.delete(vmId);
                return { ...prev, hibernating: newHibernating };
            });
            setSelectedVmForHibernate(null);
            return;
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            operationRefs.current.hibernating.delete(vmId);
            setLoadingStates(prev => {
                const newHibernating = new Set(prev.hibernating);
                newHibernating.delete(vmId);
                return { ...prev, hibernating: newHibernating };
            });
            setSelectedVmForHibernate(null);
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            setActionError(err.error);
            logError('VM hibernation network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`,
                vmId: vmId
            }, err);
            setVMs(prevVMs => 
                prevVMs.map(vm => 
                    vm.instance_id === vmId
                        ? {...vm, status: selectedVmForHibernate.status}
                        : vm
                )
            );
            operationRefs.current.hibernating.delete(vmId);
            setLoadingStates(prev => {
                const newHibernating = new Set(prev.hibernating);
                newHibernating.delete(vmId);
                return { ...prev, hibernating: newHibernating };
            });
            setSelectedVmForHibernate(null);
            return;
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            setActionError(err.error);
            setVMs(prevVMs => 
                prevVMs.map(vm => 
                    vm.instance_id === vmId
                        ? {...vm, status: selectedVmForHibernate.status}
                        : vm
                )
            );
            operationRefs.current.hibernating.delete(vmId);
            setLoadingStates(prev => {
                const newHibernating = new Set(prev.hibernating);
                newHibernating.delete(vmId);
                return { ...prev, hibernating: newHibernating };
            });
            setSelectedVmForHibernate(null);
            return;
        }
        
        // Position 4: VM not found
        if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
            notification.error('This VM no longer exists');
            setVMs(prevVMs => prevVMs.filter(v => v.instance_id !== vmId));
            setSelectedVmForDetails(null);
            operationRefs.current.hibernating.delete(vmId);
            setLoadingStates(prev => {
                const newHibernating = new Set(prev.hibernating);
                newHibernating.delete(vmId);
                return { ...prev, hibernating: newHibernating };
            });
            setSelectedVmForHibernate(null);
            return;
        }
        
        // Position 5: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            setActionError(ERROR_MESSAGES[err.code]);
            logError('VM hibernation error - mapped code', {
                code: err.code,
                vmId: vmId,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            setVMs(prevVMs => 
                prevVMs.map(vm => 
                    vm.instance_id === vmId
                        ? {...vm, status: selectedVmForHibernate.status}
                        : vm
                )
            );
            operationRefs.current.hibernating.delete(vmId);
            setLoadingStates(prev => {
                const newHibernating = new Set(prev.hibernating);
                newHibernating.delete(vmId);
                return { ...prev, hibernating: newHibernating };
            });
            setSelectedVmForHibernate(null);
            return;
        }
        
        // Position 6: Fallback
        const errorMessage = err.error || getSafeErrorMessage(err);
        setActionError(errorMessage);
        
        logError('VM hibernation error', {
          vmId: vmId,
          vmName: selectedVmForHibernate.name,
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name
        }, err);
        
        setVMs(prevVMs => 
            prevVMs.map(vm => 
                vm.instance_id === vmId
                    ? {...vm, status: selectedVmForHibernate.status}
                    : vm
            )
        );
    } finally {
        operationRefs.current.hibernating.delete(vmId);
        setLoadingStates(prev => {
            const newHibernating = new Set(prev.hibernating);
            newHibernating.delete(vmId);
            return { ...prev, hibernating: newHibernating };
        });
        setSelectedVmForHibernate(null);
    }
}, [selectedVmForHibernate, vms, loadingStates.hibernating, isAuthenticated, handleAuthError, notification]);

const handleRestoreHibernatedVM = useCallback(async () => {
    if (!selectedVmForRestore) {
        setActionError('No VM selected for restoration');
        setSelectedVmForRestore(null);
        return;
    }
    
    const vmId = selectedVmForRestore.instance_id;
    
    if (!vmId) {
        setActionError('Unable to identify VM for restoration');
        setSelectedVmForRestore(null);
        return;
    }

    if (isAdvancedRegion(selectedVmForRestore)) {
        setActionError('VM restore not available in advanced regions');
        logUserAction('vm_restore_blocked', {
            vmId: vmId,
            vmName: selectedVmForRestore.name,
            reason: 'advanced_region_not_supported'
        });
        setSelectedVmForRestore(null);
        return;
    }
    
    if (operationRefs.current.restoring.has(vmId) || 
        loadingStates.restoring.has(vmId) || 
        !isAuthenticated) {
        return;
    }
        
    const startTime = performance.now();
    
    operationRefs.current.restoring.add(vmId);
    
    setLoadingStates(prev => ({ 
        ...prev, 
        restoring: new Set([...prev.restoring, vmId])
    }));
    setActionError(null);
    setActionMessage(null);
    
    logUserAction('vm_restore_confirmed', {
      vmId: vmId,
      vmName: selectedVmForRestore.name
    });
    
    logInfo('Starting VM restoration', {
      vmId: vmId,
      vmName: selectedVmForRestore.name
    });
    
    try {
        setVMs(prevVMs => 
            prevVMs.map(vm => 
                vm.instance_id === vmId
                    ? {...vm, status: 'STARTING'}
                    : vm
            )
        );
        
        const response = await api.restoreHibernatedVM(vmId);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            setActionMessage(`VM ${sanitizeText(selectedVmForRestore.name)} is being restored from hibernation`);
            
            setTimeout(() => {
                fetchBarrackVMs();
            }, 500);
            
            setLockedVMs(prev => new Set([...prev, vmId]));
            setTimeout(() => {
                setLockedVMs(prev => {
                    const newLocked = new Set(prev);
                    newLocked.delete(vmId);
                    return newLocked;
                });
            }, 3000);
            
            logPerformance('restore_vm', duration, {
              success: true,
              vmId: vmId
            });
            
            logInfo('VM restoration initiated successfully', {
              vmId: vmId,
              vmName: selectedVmForRestore.name,
              duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vm_restore_success', {
              vmId: vmId,
              vmName: selectedVmForRestore.name,
              duration: `${duration.toFixed(2)}ms`
            });
        } else {
            setVMs(prevVMs => 
                prevVMs.map(vm => 
                    vm.instance_id === vmId
                        ? {...vm, status: selectedVmForRestore.status}
                        : vm
                )
            );
            
            const errorMessage = getSafeErrorMessage(response);
            setActionError(errorMessage);
            
            logPerformance('restore_vm', duration, {
              success: false,
              error: errorMessage
            });
            
            logError('VM restoration failed', {
              vmId: vmId,
              vmName: selectedVmForRestore.name,
              duration: `${duration.toFixed(2)}ms`,
              error: errorMessage
            });
            
            logUserAction('vm_restore_failed', {
              vmId: vmId,
              vmName: selectedVmForRestore.name,
              error: errorMessage
            });
            
            throw new Error(errorMessage);
        }
    } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
            operationRefs.current.restoring.delete(vmId);
            setLoadingStates(prev => {
                const newRestoring = new Set(prev.restoring);
                newRestoring.delete(vmId);
                return { ...prev, restoring: newRestoring };
            });
            setSelectedVmForRestore(null);
            return;
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            operationRefs.current.restoring.delete(vmId);
            setLoadingStates(prev => {
                const newRestoring = new Set(prev.restoring);
                newRestoring.delete(vmId);
                return { ...prev, restoring: newRestoring };
            });
            setSelectedVmForRestore(null);
            return;
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            setActionError(err.error);
            logError('VM restoration network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`,
                vmId: vmId
            }, err);
            setVMs(prevVMs => 
                prevVMs.map(vm => 
                    vm.instance_id === vmId
                        ? {...vm, status: selectedVmForRestore.status}
                        : vm
                )
            );
            operationRefs.current.restoring.delete(vmId);
            setLoadingStates(prev => {
                const newRestoring = new Set(prev.restoring);
                newRestoring.delete(vmId);
                return { ...prev, restoring: newRestoring };
            });
            setSelectedVmForRestore(null);
            return;
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            setActionError(err.error);
            setVMs(prevVMs => 
                prevVMs.map(vm => 
                    vm.instance_id === vmId
                        ? {...vm, status: selectedVmForRestore.status}
                        : vm
                )
            );
            operationRefs.current.restoring.delete(vmId);
            setLoadingStates(prev => {
                const newRestoring = new Set(prev.restoring);
                newRestoring.delete(vmId);
                return { ...prev, restoring: newRestoring };
            });
            setSelectedVmForRestore(null);
            return;
        }
        
        // Position 4: VM not found
        if (err.status === 404 || err.code === 'VM_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
            notification.error('This VM no longer exists');
            setVMs(prevVMs => prevVMs.filter(v => v.instance_id !== vmId));
            setSelectedVmForDetails(null);
            operationRefs.current.restoring.delete(vmId);
            setLoadingStates(prev => {
                const newRestoring = new Set(prev.restoring);
                newRestoring.delete(vmId);
                return { ...prev, restoring: newRestoring };
            });
            setSelectedVmForRestore(null);
            return;
        }
        
        // Position 5: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            setActionError(ERROR_MESSAGES[err.code]);
            logError('VM restoration error - mapped code', {
                code: err.code,
                vmId: vmId,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            setVMs(prevVMs => 
                prevVMs.map(vm => 
                    vm.instance_id === vmId
                        ? {...vm, status: selectedVmForRestore.status}
                        : vm
                )
            );
            operationRefs.current.restoring.delete(vmId);
            setLoadingStates(prev => {
                const newRestoring = new Set(prev.restoring);
                newRestoring.delete(vmId);
                return { ...prev, restoring: newRestoring };
            });
            setSelectedVmForRestore(null);
            return;
        }
        
        // Position 6: Fallback
        const errorMessage = err.error || getSafeErrorMessage(err);
        setActionError(errorMessage);
        
        logError('VM restoration error', {
          vmId: vmId,
          vmName: selectedVmForRestore.name,
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name
        }, err);
        
        setVMs(prevVMs => 
            prevVMs.map(vm => 
                vm.instance_id === vmId
                    ? {...vm, status: selectedVmForRestore.status}
                    : vm
            )
        );
    } finally {
        operationRefs.current.restoring.delete(vmId);
        setLoadingStates(prev => {
            const newRestoring = new Set(prev.restoring);
            newRestoring.delete(vmId);
            return { ...prev, restoring: newRestoring };
        });
        setSelectedVmForRestore(null);
    }
}, [selectedVmForRestore, vms, loadingStates.restoring, isAuthenticated, handleAuthError, notification]);



const handleDeleteVM = useCallback((vmId) => {
    const deletedVm = vms.find(vm => vm.instance_id === vmId);
    setVMs(vms.filter(vm => vm.instance_id !== vmId));
    
    logUserAction('vm_deleted_from_list', {
      vmId,
      vmName: deletedVm?.name,
      remainingVmCount: vms.length - 1
    });
    
    logInfo('VM removed from list', {
      vmId,
      vmName: deletedVm?.name,
      totalVms: vms.length - 1
    });
}, [vms]);

const handleLogout = useCallback(async () => {
    if (!isAuthenticated) return;

    const startTime = performance.now();
    
    logUserAction('logout_initiated', {
      source: 'dashboard',
      vmCount: vms.length,
      barrackId
    });
    
    logInfo('Starting logout process', {
      vmCount: vms.length,
      hasBarrackId: !!barrackId
    });

    try {
        const response = await api.logoutUser();
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        localStorage.removeItem('user_email');
        localStorage.removeItem('barrack_id');
        localStorage.removeItem('access_token');
        setBarrackId('Not Available');
        
        window.dispatchEvent(
            new CustomEvent('auth:stateChanged', {
                detail: {
                    authenticated: false,
                    user: null,
                    isAuthenticated: false,
                    status: 'LOGGED_OUT'
                }
            })
        );
        
        closeEventSource();
        
        logPerformance('logout', duration, {
          success: true
        });
        
        logInfo('Logout completed successfully', {
          duration: `${duration.toFixed(2)}ms`
        });
        
        logUserAction('logout_success', {
          duration: `${duration.toFixed(2)}ms`
        });
        
        navigate('/', { replace: true });
    } catch (err) {
        if (err.name === 'AbortError') {
            return;
        }
        
        if (err.status === 401) {
            handleAuthError();
            return;
        }
        
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        logError('Logout error occurred', {
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name
        }, err);
        
        window.dispatchEvent(
            new CustomEvent('auth:stateChanged', {
                detail: {
                    authenticated: false,
                    user: null,
                    isAuthenticated: false,
                    status: 'LOGGED_OUT'
                }
            })
        );
        
        closeEventSource();
        
        logUserAction('logout_forced', {
          reason: 'api_error',
          duration: `${duration.toFixed(2)}ms`
        });
        
        navigate('/');
    }
}, [closeEventSource, navigate, isAuthenticated, vms.length, barrackId, handleAuthError]);
    const handleVmSelect = useCallback((vm) => {
        if (!isAuthenticated) {
            logUserAction('vm_select_redirect_signup', {
              vmId: vm.id,
              vmName: vm.name
            });
            navigate('/signup');
            return;
        }
        
        const previousVm = selectedVmForDetails;
        setSelectedVmForDetails(vm);
        setVmSelectorDropdownOpen(false);
        
        logUserAction('vm_selected', {
          previousVmId: previousVm?.id,
          newVmId: vm.id,
          newVmName: vm.name,
          newVmStatus: vm.status
        });
        
        logInfo('VM selection changed', {
          from: previousVm?.name,
          to: vm.name,
          status: vm.status
        });
    }, [isAuthenticated, navigate, selectedVmForDetails]);

  const handleViewDetails = useCallback((vm) => {
    if (!isAuthenticated) {
        logUserAction('view_details_redirect_signup', {
          vmId: vm.instance_id,
          vmName: vm.name
        });
        navigate('/signup');
        return;
    }
    
    logUserAction('vm_details_viewed', {
      vmId: vm.instance_id,
      vmName: vm.name,
      vmStatus: vm.status
    });
    
    logInfo('VM details modal opened', {
      vmId: vm.instance_id,
      vmName: vm.name
    });
    
    setSelectedVM(vm);
    setShowVMDetails(true);
}, [isAuthenticated, navigate]);

    const getStatusBadgeClass = useCallback((status) => {
        if (status === 'RUNNING') return 'bg-green-500';
        if (status === 'HIBERNATED') return 'bg-yellow-500';
        if (status === 'SHUTOFF') return 'bg-red-500';
        return 'bg-gray-500';
    }, []);

    const getStatusTextColor = useCallback((status) => {
        switch(status) {
          case 'RUNNING':
           return 'bg-[#1a1a1a] border border-[#cad9e3]/30 text-gray-300';
          case 'HIBERNATED':
            return 'bg-[#1a1a1a] border border-[#cad9e3]/30 text-gray-300';
          case 'SHUTOFF':
            return 'bg-[#1a1a1a] border border-[#cad9e3]/30 text-gray-300';
          default:
            return 'bg-[#1a1a1a] border border-[#cad9e3]/30 text-gray-400';
        }
    }, []);

    /**
     * Calculate VM pricing based on specs (gpu_type, gpu_count).
     * 
     * VM Data Structure:
     * - specs.gpu_type: GPU model name (e.g., "RTX-A6000", "H100-SXM5-80GB")
     * - specs.gpu_count: Number of GPUs
     * - specs.cpu: CPU cores
     * - specs.ram: RAM in GB
     */
    const calculateVmPricing = useCallback((vm) => {
        if (!vm || !vm.specs || !vm.specs.gpu_type) {
            return { hourly: 0, symbol: currencyUtils.getSymbol(userCurrency) };
        }

        const currency = userCurrency;
        const currencySymbol = currencyUtils.getSymbol(currency);
        const gpuType = vm.specs.gpu_type;
        const gpuCount = vm.specs.gpu_count || 1;
            
        let hourlyRate = 0;
        
        if (GPU_PRICING[gpuType]) {
            hourlyRate = GPU_PRICING[gpuType].price[currency] * gpuCount;
        } else {
            for (const [key, value] of Object.entries(GPU_PRICING)) {
                if (gpuType.includes(key) || key.includes(gpuType)) {
                    hourlyRate = value.price[currency] * gpuCount;
                    break;
                }
            }
        }
        
        return {
            hourly: hourlyRate,
            symbol: currencySymbol
        };
   }, [userCurrency]);

    const handleRefresh = useCallback(() => {
        if (retryAttempts.current >= maxRetries) {
            setError('Maximum refresh attempts reached. Please wait before trying again.');
            logError('Maximum dashboard refresh attempts exceeded', {
              attempts: retryAttempts.current,
              maxRetries,
              vmCount: vms.length
            });
            return;
        }
        
        retryAttempts.current++;
        
        logUserAction('dashboard_refresh_initiated', {
          vmCount: vms.length,
          hasError: !!error,
          isLoading,
          attempt: retryAttempts.current
        });
        
        logInfo('Dashboard refresh initiated by user', {
          attempt: retryAttempts.current,
          maxRetries
        });
        
        window.location.reload();
    }, [vms.length, error, isLoading]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchUserCurrency();
        }
    }, [fetchUserCurrency, isAuthenticated]);

useEffect(() => {
    const fetchData = async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        abortControllerRef.current = new AbortController();
        
        const initializeDashboard = async () => {
            const startTime = performance.now();
            setIsLoading(true);
            
            logInfo('Dashboard initialization started', {
              isAuthenticated,
              userEmail: userEmail ? 'has_email' : 'no_email'
            });
            
            try {
                if (isAuthenticated) {
                    const results = await Promise.allSettled([
                        api.getUserProfile(),
                        fetchBarrackVMs(),
                        fetchCreditBalance(),
                        userEmail && fetchBarrackId()
                    ].filter(Boolean));
                    
                    const endTime = performance.now();
                    const duration = endTime - startTime;
                    
                    // Handle profile result
                    const profileResult = results[0];
                    if (profileResult?.status === 'fulfilled' && profileResult.value?.success) {
                        if (profileResult.value.profile?.preferred_currency) {
                            setUserCurrency(profileResult.value.profile.preferred_currency);
                        }
                        
                        logInfo('User profile loaded successfully', {
                          currency: profileResult.value.profile?.preferred_currency
                        });
                    }
                    
                    // Check if ALL critical calls failed
                    const allFailed = results.every(r => 
                        r.status === 'rejected' || 
                        (r.status === 'fulfilled' && r.value && !r.value.success && !r.value.aborted && !r.value.skipped)
                    );
                    
                    if (allFailed) {
                        if (!navigator.onLine) {
                            setError(ERROR_MESSAGES['NETWORK_ERROR']);
                        } else {
                            const networkCodes = ['NETWORK_ERROR', 'SERVER_UNREACHABLE', 'OFFLINE', 'TIMEOUT', 'NO_RESPONSE'];
                            const networkErrorResult = results.find(r => 
                                r.status === 'fulfilled' && networkCodes.includes(r.value?.code)
                            );
                            
                            if (networkErrorResult) {
                                setError(ERROR_MESSAGES['NETWORK_ERROR']);
                            } else {
                                setError('Unable to load dashboard data. Please refresh.');
                            }
                        }
                    }
                    retryAttempts.current = 0;
                    
                    logPerformance('dashboard_initialization', duration, {
                      success: !allFailed,
                      isAuthenticated: true,
                      resultsCount: results.length
                    });
                } else {
                    const endTime = performance.now();
                    const duration = endTime - startTime;
                    
                    logPerformance('dashboard_initialization', duration, {
                      success: true,
                      isAuthenticated: false
                    });
                    
                    setIsLoading(false);
                }
                
            } catch (err) {
                const endTime = performance.now();
                const duration = endTime - startTime;
                
                if (err.name === 'AbortError') {
                    return;
                }
                
                // Position 1: Authentication error
                if (err.status === 401) {
                    handleAuthError();
                    setIsLoading(false);
                    return;
                }
                
                // Position 2: Network error
                const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
                if (networkErrorCodes.includes(err.code)) {
                    setError(err.error);
                    logError('Dashboard initialization network error', { 
                        code: err.code,
                        duration: `${duration.toFixed(2)}ms`
                    }, err);
                    setIsLoading(false);
                    return;
                }
                
                // Position 3: Rate limit
                if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
                    setError(err.error);
                    setIsLoading(false);
                    return;
                }
                
                // Position 4: Mapped error code
                if (err.code && ERROR_MESSAGES[err.code]) {
                    setError(ERROR_MESSAGES[err.code]);
                    logError('Dashboard initialization error - mapped code', {
                        code: err.code,
                        duration: `${duration.toFixed(2)}ms`
                    }, err);
                    setIsLoading(false);
                    return;
                }
                
                // Position 5: Fallback
                const safeMessage = err.error || getSafeErrorMessage(err);
                setError(safeMessage);
                
                logPerformance('dashboard_initialization', duration, {
                  success: false,
                  error: safeMessage
                });
                
                logError('Dashboard initialization failed', {
                  duration: `${duration.toFixed(2)}ms`,
                  errorName: err.name
                }, err);
            } finally {
                setIsLoading(false);
            }
        };
        
        if (isInitialized) {
            initializeDashboard();
        }
    };

    fetchData();
    
    return () => {
        closeEventSource();
        if (fetchVMsTimeoutRef.current) {
            clearTimeout(fetchVMsTimeoutRef.current);
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        if (deploymentTimeoutRef.current) {
            clearTimeout(deploymentTimeoutRef.current);
        }
        
        logInfo('Dashboard cleanup completed');
    };
}, [isAuthenticated, isInitialized, userEmail, handleAuthError]);
    useEffect(() => {
        if (actionMessage || actionError) {
            const timer = setTimeout(() => {
                setActionMessage(null);
                setActionError(null);
                
                logInfo('Action message/error cleared', {
                  hadMessage: !!actionMessage,
                  hadError: !!actionError
                });
            }, 5000);
            
            return () => clearTimeout(timer);
        }
    }, [actionMessage, actionError]);

useEffect(() => {
    if (isAuthenticated && vms.length > 0 && !selectedVmForDetails) {
        const firstVm = vms[0];
        setSelectedVmForDetails(firstVm);
        
        logUserAction('vm_auto_selected', {
          vmId: firstVm.instance_id,
          vmName: firstVm.name,
          reason: 'first_vm_in_list'
        });
        
        logInfo('First VM auto-selected', {
          vmId: firstVm.instance_id,
          vmName: firstVm.name,
          totalVms: vms.length
        });
    }
}, [vms, isAuthenticated]);
    
 useEffect(() => {
    if (selectedVmForDetails && vms.length > 0) {
        const updatedVm = vms.find(vm => vm.instance_id === selectedVmForDetails.instance_id);
        if (updatedVm && (
            updatedVm.status !== selectedVmForDetails.status ||
            updatedVm.floating_ip !== selectedVmForDetails.floating_ip ||
            JSON.stringify(updatedVm.specs) !== JSON.stringify(selectedVmForDetails.specs)
        )) {
            setSelectedVmForDetails(updatedVm);
            
            logInfo('VM details synced with fresh API data', {
                vmId: updatedVm.instance_id,
                oldStatus: selectedVmForDetails.status,
                newStatus: updatedVm.status
            });
        }
    }
}, [vms, selectedVmForDetails]);
const fetchBarrackId = async () => {
    if (!userEmail || isGettingBarrackId.current || !isAuthenticated) return { success: false, skipped: true };
    
    const startTime = performance.now();
    
    try {
        isGettingBarrackId.current = true;
        
        logInfo('Fetching barrack ID', { userEmail: 'has_email' });
        
        const response = await api.getBarrackId(sanitizeText(userEmail), false);
        
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success && response.barrack_id) {
            const sanitizedBarrackId = sanitizeText(response.barrack_id);
            setBarrackId(sanitizedBarrackId);
            localStorage.setItem('barrack_id', sanitizedBarrackId);
            
            logPerformance('fetch_barrack_id', duration, {
                success: true
            });
            
            logInfo('Barrack ID fetched successfully', {
                duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('barrack_id_loaded', {
                duration: `${duration.toFixed(2)}ms`
            });
            
            return response;
        } else {
            const errorMessage = getSafeErrorMessage(response);
            
            logError('Failed to fetch barrack ID - invalid response', {
                duration: `${duration.toFixed(2)}ms`,
                error: errorMessage
            });
            
            return response;
        }
    } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
            isGettingBarrackId.current = false;
            return { success: false, aborted: true };
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            isGettingBarrackId.current = false;
            return { success: false, auth_error: true };
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            logError('Fetch barrack ID network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            isGettingBarrackId.current = false;
            return { success: false, error: err.error, code: err.code };
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            isGettingBarrackId.current = false;
            return { success: false, error: err.error };
        }
        
        // Position 4: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            logError('Fetch barrack ID error - mapped code', {
              code: err.code,
              duration: `${duration.toFixed(2)}ms`
            }, err);
            isGettingBarrackId.current = false;
            return { success: false, error: ERROR_MESSAGES[err.code] };
        }
        
        // Position 5: Fallback
        const errorMessage = err.error || getSafeErrorMessage(err);
        
        logPerformance('fetch_barrack_id', duration, {
            success: false,
            error: errorMessage
        });
        
        logError('Failed to fetch barrack ID', {
            duration: `${duration.toFixed(2)}ms`,
            errorName: err.name
        }, err);
        
        return { success: false, error: errorMessage };
    } finally {
        isGettingBarrackId.current = false;
    }
};

const fetchBarrackVMs = async () => {
    if (isFetchingVMs.current || !isAuthenticated) return { success: false, skipped: true };
    
    // Apply cached operation first
    const cachedOp = getVMOperationCache();
    if (cachedOp) {
        setVMs(prevVMs => 
            prevVMs.map(v => 
                v.instance_id === cachedOp.vmId 
                    ? {...v, status: cachedOp.status} 
                    : v
            )
        );
        setLockedVMs(prev => new Set([...prev, cachedOp.vmId]));
        
        logInfo('Applied cached VM operation', {
            vmId: cachedOp.vmId,
            status: cachedOp.status
        });
    }
    
    const startTime = performance.now();
    
    try {
        isFetchingVMs.current = true;
        setIsLoading(true);
        
        logInfo('Fetching user VMs');
        
        const response = await api.getVMs();
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.success) {
            const vmData = response.instances || [];
            setVMs(vmData);
            
            // Clear cache after fresh data loaded
            clearVMOperationCache();
            
            retryAttempts.current = 0;
            
            logPerformance('fetch_vms', duration, {
              success: true,
              vmCount: vmData.length
            });
            
            logInfo('VMs loaded successfully', {
              vmCount: vmData.length,
              duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('vms_loaded', {
              vmCount: vmData.length,
              duration: `${duration.toFixed(2)}ms`
            });
            
            return response;
        } else {
            const errorMessage = getSafeErrorMessage(response);
            
            logPerformance('fetch_vms', duration, {
              success: false,
              error: errorMessage
            });
            
            logError('VM fetch failed', {
              duration: `${duration.toFixed(2)}ms`,
              error: errorMessage
            });
            
            return response;
        }
    } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
            setIsLoading(false);
            isFetchingVMs.current = false;
            return { success: false, aborted: true };
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            setIsLoading(false);
            isFetchingVMs.current = false;
            return { success: false, auth_error: true };
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            logError('VM fetch network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            setIsLoading(false);
            isFetchingVMs.current = false;
            return { success: false, error: err.error, code: err.code };
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            setIsLoading(false);
            isFetchingVMs.current = false;
            return { success: false, error: err.error };
        }
        
        // Position 4: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            logError('VM fetch error - mapped code', {
              code: err.code,
              duration: `${duration.toFixed(2)}ms`
            }, err);
            setIsLoading(false);
            isFetchingVMs.current = false;
            return { success: false, error: ERROR_MESSAGES[err.code] };
        }
        
        // Position 5: Fallback
        const errorMessage = err.error || getSafeErrorMessage(err);
        
        logError('VM fetch error', {
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name
        }, err);
        
        return { success: false, error: errorMessage };
    } finally {
        setIsLoading(false);
        isFetchingVMs.current = false;
    }
};

const fetchCreditBalance = async () => {
    if (isFetchingBalance.current || !isAuthenticated) return { success: false, skipped: true };
   
    const startTime = performance.now();
    
    try {
        isFetchingBalance.current = true;
        
        logInfo('Fetching credit balance');
       
        const response = await api.getCreditBalance();
        const endTime = performance.now();
        const duration = endTime - startTime;
       
        if (response.success) {
            const newBalance = {
                inr: response.balance.inr || 0,
                usd: response.balance.usd || 0,
                eur: response.balance.eur || 0,
                locked_inr: response.available_balance?.inr 
                    ? response.balance.inr - response.available_balance.inr 
                    : 0,
                locked_usd: response.available_balance?.usd 
                    ? response.balance.usd - response.available_balance.usd 
                    : 0,
                locked_eur: response.available_balance?.eur 
                    ? response.balance.eur - response.available_balance.eur 
                    : 0
            };
            
            setCreditBalance(newBalance);
                        setCreditBalanceError(null);

            setUserCurrency(response.account_currency);
            setPreferredCurrency(response.account_currency);

            logPerformance('fetch_credit_balance', duration, {
              success: true,
              currency: response.account_currency,
              hasLockedFunds: newBalance.locked_usd > 0 || newBalance.locked_inr > 0 || newBalance.locked_eur > 0
            });

            logInfo('Credit balance loaded successfully', {
              currency: response.account_currency,
              duration: `${duration.toFixed(2)}ms`,
              hasLockedFunds: newBalance.locked_usd > 0 || newBalance.locked_inr > 0 || newBalance.locked_eur > 0
            });

            logUserAction('credit_balance_loaded', {
              currency: response.account_currency,
              duration: `${duration.toFixed(2)}ms`
            });
            
            return response;
     } else {
            const errorMessage = getSafeErrorMessage(response);
            
            if (response.code === 'INCOMPLETE_PROFILE') {
                setCreditBalanceError({
                    message: 'Please complete your profile.',
                    redirectTo: '/account'
                });
                logError('Credit balance fetch failed - incomplete profile', {
                    duration: `${duration.toFixed(2)}ms`,
                    code: response.code,
                    redirectTo: response.redirect_to
                });
            } else {
                logError('Credit balance fetch failed', {
                    duration: `${duration.toFixed(2)}ms`,
                    error: errorMessage
                });
            }
            
            return response;
        }
   } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
            isFetchingBalance.current = false;
            return { success: false, aborted: true };
        }
        
        // Incomplete profile
        if (err.code === 'INCOMPLETE_PROFILE') {
            setCreditBalanceError({
                message: 'Please complete your profile.',
                redirectTo: '/account'
            });
            logError('Credit balance fetch failed - incomplete profile', {
                duration: `${duration.toFixed(2)}ms`,
                code: err.code
            }, err);
            isFetchingBalance.current = false;
            return { success: false, error: err.error, code: err.code };
        }
        
        // Position 1: Authentication error
        if (err.status === 401) {
            handleAuthError();
            isFetchingBalance.current = false;
            return { success: false, auth_error: true };
        }
        
        // Position 2: Network error
        const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
        if (networkErrorCodes.includes(err.code)) {
            logError('Credit balance network error', { 
                code: err.code,
                duration: `${duration.toFixed(2)}ms`
            }, err);
            isFetchingBalance.current = false;
            return { success: false, error: err.error, code: err.code };
        }
        
        // Position 3: Rate limit
        if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
            isFetchingBalance.current = false;
            return { success: false, error: err.error };
        }
        
        // Position 4: Mapped error code
        if (err.code && ERROR_MESSAGES[err.code]) {
            logError('Credit balance fetch error - mapped code', {
              code: err.code,
              duration: `${duration.toFixed(2)}ms`
            }, err);
            isFetchingBalance.current = false;
            return { success: false, error: ERROR_MESSAGES[err.code] };
        }
        
        // Position 5: Fallback
        const errorMessage = err.error || getSafeErrorMessage(err);
        
        logError('Credit balance fetch error', {
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name
        }, err);
        
        return { success: false, error: errorMessage };
    } finally {
        isFetchingBalance.current = false;
    }
};   if (!isInitialized || authLoading) {
    return (
        <div className="min-h-screen bg-black text-white">
            <style>{`
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                @keyframes pulse-glow {
                    0%, 100% { opacity: 0.4; }
                    50% { opacity: 0.8; }
                }
                .shimmer-bg {
                    background: linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%);
                    background-size: 200% 100%;
                    animation: shimmer 1.5s ease-in-out infinite;
                }
                .pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
            `}</style>
            
            {!isMobile && !isTablet ? (
                // Desktop Skeleton
                <div style={{ display: 'flex', height: '100vh', background: '#000000', position: 'relative', overflow: 'hidden' }}>
                    {/* Background decorations */}
                    <div style={{ position: 'absolute', top: '80px', right: '10%', width: '384px', height: '384px', background: '#1a1a1a', opacity: 0.25, borderRadius: '50%', filter: 'blur(96px)' }}></div>
                    <div style={{ position: 'absolute', top: '-40px', left: '-80px', width: '288px', height: '288px', background: '#1a1a1a', opacity: 0.2, borderRadius: '50%', filter: 'blur(96px)' }}></div>
                    
                    <div style={{ flex: 1, display: 'flex', width: '100%', height: '100%', overflow: 'hidden', position: 'relative', zIndex: 10 }}>
                        {/* Main Content - 52% */}
                        <div style={{ width: '52%', padding: '24px', background: 'transparent', overflowY: 'auto', marginTop: '-7cm', marginLeft: '-1cm', transform: 'translateX(0.4cm)' }}>
                            <div style={{ height: '32px', width: '128px', marginBottom: '24px' }} className="shimmer-bg"></div>
                            
                            {/* VM Details Card Skeleton */}
                            <div style={{ background: 'linear-gradient(to bottom right, #0a0c0a, #121a15)', border: '1px solid rgba(202,217,227,0.15)', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                                {/* Header */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <div style={{ width: '48px', height: '48px', borderRadius: '12px' }} className="shimmer-bg pulse-glow"></div>
                                        <div>
                                            <div style={{ height: '28px', width: '200px', marginBottom: '8px' }} className="shimmer-bg"></div>
                                            <div style={{ height: '16px', width: '300px' }} className="shimmer-bg"></div>
                                        </div>
                                    </div>
                                    <div style={{ height: '32px', width: '80px', borderRadius: '6px' }} className="shimmer-bg"></div>
                                </div>
                                
                                {/* Specs Grid */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
                                    {[1,2,3,4].map(i => (
                                        <div key={i} style={{ background: 'rgba(18, 26, 21, 0.6)', border: '1px solid rgba(26, 42, 26, 0.3)', borderRadius: '8px', padding: '16px' }}>
                                            <div style={{ height: '12px', width: '60px', marginBottom: '8px' }} className="shimmer-bg"></div>
                                            <div style={{ height: '24px', width: '40px' }} className="shimmer-bg"></div>
                                        </div>
                                    ))}
                                </div>
                                
                                {/* Info Row */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <div style={{ height: '16px', width: '64px' }} className="shimmer-bg"></div>
                                        <div style={{ height: '16px', width: '96px' }} className="shimmer-bg"></div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <div style={{ height: '16px', width: '56px' }} className="shimmer-bg"></div>
                                        <div style={{ height: '16px', width: '120px' }} className="shimmer-bg"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* VM Selector & Actions - 26% */}
                        <div style={{ width: '26%', padding: '24px', background: 'transparent', marginTop: '-2cm', paddingRight: 'calc(1.5rem - 1.5cm)', transform: 'translateX(0.6cm)' }}>
                            {/* VM Selector Skeleton */}
                            <div style={{ background: 'linear-gradient(to bottom right, #0a0c0a, #121a15)', border: '1px solid rgba(202,217,227,0.15)', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                                <div style={{ height: '20px', width: '120px', marginBottom: '16px' }} className="shimmer-bg"></div>
                                <div style={{ height: '48px', width: '100%', borderRadius: '8px', marginBottom: '12px' }} className="shimmer-bg"></div>
                            </div>
                            
                            {/* Actions Skeleton */}
                            <div style={{ background: 'linear-gradient(to bottom right, #0a0c0a, #121a15)', border: '1px solid rgba(202,217,227,0.15)', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                                <div style={{ height: '20px', width: '100px', marginBottom: '16px' }} className="shimmer-bg"></div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    {[1,2,3,4,5,6].map(i => (
                                        <div key={i} style={{ height: '44px', borderRadius: '8px' }} className="shimmer-bg"></div>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Pricing Skeleton */}
                            <div style={{ background: 'linear-gradient(to bottom right, #0a0c0a, #121a15)', border: '1px solid rgba(202,217,227,0.15)', borderRadius: '12px', padding: '20px' }}>
                                <div style={{ height: '20px', width: '140px', marginBottom: '16px' }} className="shimmer-bg"></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                    <div style={{ height: '16px', width: '80px' }} className="shimmer-bg"></div>
                                    <div style={{ height: '16px', width: '60px' }} className="shimmer-bg"></div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div style={{ height: '16px', width: '80px' }} className="shimmer-bg"></div>
                                    <div style={{ height: '16px', width: '60px' }} className="shimmer-bg"></div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Sidebar */}
                        <div style={{ width: '320px', padding: '24px', background: 'transparent', borderLeft: 'none', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            {/* Account Info Skeleton */}
                            <div style={{ background: 'linear-gradient(to bottom right, #0a0c0a, #121a15)', border: '1px solid rgba(202,217,227,0.15)', borderRadius: '12px', padding: '20px' }}>
                                <div style={{ height: '20px', width: '100px', marginBottom: '16px' }} className="shimmer-bg"></div>
                                <div style={{ height: '16px', width: '100%', marginBottom: '8px' }} className="shimmer-bg"></div>
                                <div style={{ height: '16px', width: '80%', marginBottom: '8px' }} className="shimmer-bg"></div>
                                <div style={{ height: '16px', width: '90%' }} className="shimmer-bg"></div>
                            </div>
                            
                            {/* Quick Actions Skeleton */}
                            <div style={{ background: 'linear-gradient(to bottom right, #0a0c0a, #121a15)', border: '1px solid rgba(202,217,227,0.15)', borderRadius: '12px', padding: '20px' }}>
                                <div style={{ height: '20px', width: '120px', marginBottom: '16px' }} className="shimmer-bg"></div>
                                {[1,2,3].map(i => (
                                    <div key={i} style={{ height: '40px', borderRadius: '8px', marginBottom: '12px' }} className="shimmer-bg"></div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                // Mobile/Tablet Skeleton
                <div style={{ minHeight: '100vh', background: 'black', padding: isMobile ? '16px' : '24px' }}>
                    {/* VM Card Skeleton */}
                    <div style={{ background: 'linear-gradient(to bottom right, #0a0c0a, #121a15)', border: '1px solid rgba(202,217,227,0.15)', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '8px' }} className="shimmer-bg pulse-glow"></div>
                            <div style={{ flex: 1 }}>
                                <div style={{ height: '24px', width: '60%', marginBottom: '8px' }} className="shimmer-bg"></div>
                                <div style={{ height: '16px', width: '80%' }} className="shimmer-bg"></div>
                            </div>
                        </div>
                        
                        {/* Mobile Specs Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            {[1,2,3,4].map(i => (
                                <div key={i} style={{ background: 'rgba(18, 26, 21, 0.6)', border: '1px solid rgba(26, 42, 26, 0.3)', borderRadius: '8px', padding: '12px' }}>
                                    <div style={{ height: '12px', width: '50px', marginBottom: '8px' }} className="shimmer-bg"></div>
                                    <div style={{ height: '20px', width: '35px' }} className="shimmer-bg"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Actions Skeleton */}
                    <div style={{ background: 'linear-gradient(to bottom right, #0a0c0a, #121a15)', border: '1px solid rgba(202,217,227,0.15)', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                        <div style={{ height: '20px', width: '100px', marginBottom: '16px' }} className="shimmer-bg"></div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            {[1,2,3,4].map(i => (
                                <div key={i} style={{ height: '40px', borderRadius: '8px' }} className="shimmer-bg"></div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Account Info Skeleton */}
                    <div style={{ background: 'linear-gradient(to bottom right, #0a0c0a, #121a15)', border: '1px solid rgba(202,217,227,0.15)', borderRadius: '12px', padding: '20px' }}>
                        <div style={{ height: '20px', width: '120px', marginBottom: '16px' }} className="shimmer-bg"></div>
                        <div style={{ height: '16px', width: '100%', marginBottom: '8px' }} className="shimmer-bg"></div>
                        <div style={{ height: '16px', width: '85%', marginBottom: '8px' }} className="shimmer-bg"></div>
                        <div style={{ height: '16px', width: '75%' }} className="shimmer-bg"></div>
                    </div>
                </div>
            )}
        </div>
    );
}

    return (
        <ResponsiveWrapper
            pageDescription="Dashboard - VM Instance Management"
            isRefreshing={isLoading}
            error={error}
            isLoading={isLoading}
            loadingText="Loading dashboard..."
        >
            {!isMobile && !isTablet ? (
                // Desktop layout
                <div className="flex flex-col h-screen bg-[#000000] text-white relative overflow-hidden">
                    <div className="absolute top-20 right-[10%] w-96 h-96 bg-[#1a1a1a] opacity-25 rounded-full blur-3xl"></div>
                    <div className="absolute -top-10 -left-20 w-72 h-72 bg-[#1a1a1a] opacity-20 rounded-full blur-3xl"></div>
                    
                    <div className="absolute bottom-0 left-0 w-full h-[70%] bg-[#cad9e3] opacity-[0.07] rounded-t-[100%] blur-3xl"></div>
                    
                    <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] z-0"></div>
                   
                    <div className="relative z-10 flex flex-1 w-full h-full">
                        <div className="flex flex-1 w-full h-full overflow-hidden">
                          
                            <div className="w-[52%] p-6 bg-transparent overflow-y-auto" style={{ marginTop: "-7cm", marginLeft: "-1cm", transform: "translateX(0.4cm)" }}>
                                <h1 className="text-2xl font-bold mb-6 text-white">Instance</h1>
                
                                
                                {error && (
                                  <div className="bg-[#ef4444]/20 border border-[#ef4444] text-[#ef4444] px-4 py-3 rounded-md mb-4">
                                    <AlertTriangle className="inline-block mr-2 h-4 w-4" />
                                    Error: {sanitizeText(error)}
                                  </div>
                                )}
                                
                                {isLoading ? (
                                  <div className="loading-state">
                                    <p>Loading VMs...</p>
                                  </div>
                                ) : error ? (
                                  <div className="error-state">
                                    <p className="flex items-center text-[#ef4444]">
                                      <AlertTriangle className="inline-block mr-2 h-4 w-4" />
                                      Error: {sanitizeText(error)}
                                    </p>
                                    <button 
                                      onClick={() => {
                                        logUserAction('error_retry_clicked', { 
                                          error, 
                                          isAuthenticated,
                                          source: 'desktop_layout'
                                        });
                                        isAuthenticated ? fetchBarrackVMs() : navigate('/signup');
                                      }}
                                      className="mt-4 px-4 py-2 bg-[#cad9e3] text-[#040911] rounded-md font-medium hover:bg-[#b8c7d4] transition-colors"
                                    >
                                      {isAuthenticated ? 'Retry' : 'Sign Up'}
                                    </button>
                                  </div>
                                ) : (
                                  <VMDetailsSection
                                    selectedVmForDetails={selectedVmForDetails}
                                    getStatusBadgeClass={getStatusBadgeClass}
                                    getStatusTextColor={getStatusTextColor}
                                    calculateVmPricing={calculateVmPricing}
                                    navigate={navigate}
                                    isAuthenticated={isAuthenticated}
                                    lockedVMs={lockedVMs}
                                  />
                                )}
                                
                                {isAuthenticated && vms.length === 0 && !isLoading && !error && (
                                  <div className="no-servers">
                                    <p>No VMs deployed yet</p>
                                    <button 
                                      className="deploy-btn"
                                      onClick={() => {
                                        logUserAction('deploy_new_vm_clicked', {
                                          source: 'no_vms_message'
                                        });
                                        navigate('/deploy');
                                      }}
                                    >
                                      Deploy New VM
                                    </button>
                                  </div>
                                )}
                            </div>
                            
                            <div className="w-[26%] p-6 bg-transparent" style={{ marginTop: selectedVmForDetails ? '-2cm' : '-14cm', paddingRight: "calc(1.5rem - 1.5cm)", transform: "translateX(0.6cm)" }}>
                                <div className="flex justify-between items-center mb-5">
                                </div>
                                
                                <VMSelectorSection
                                  vms={vms}
                                  selectedVmForDetails={selectedVmForDetails}
                                  vmSelectorDropdownOpen={vmSelectorDropdownOpen}
                                  setVmSelectorDropdownOpen={setVmSelectorDropdownOpen}
                                  handleVmSelect={handleVmSelect}
                                  getStatusTextColor={getStatusTextColor}
                                  navigate={navigate}
                                  isAuthenticated={isAuthenticated}
                                />
                                
                                <VMActionsSection
                                  selectedVmForDetails={selectedVmForDetails}
                                  handleViewDetails={handleViewDetails}
                                  handleHibernateClick={handleHibernateClick}
                                  handleRestoreClick={handleRestoreClick}
                                  setShowDeleteModal={setShowDeleteModal}
                                  handleTakeSnapshotClick={handleTakeSnapshotClick}
                                  handleCreateVolumeClick={handleCreateVolumeClick}
                                  handleStartClick={handleStartClick}
                                  handleStopClick={handleStopClick}
                                  handleRebootClick={handleRebootClick}
                                  isAuthenticated={isAuthenticated}
                                  isHibernating={selectedVmForDetails && loadingStates.hibernating.has(selectedVmForDetails.id)}
                                  isRestoring={selectedVmForDetails && loadingStates.restoring.has(selectedVmForDetails.id)}
                                  isDeleting={selectedVmForDetails && loadingStates.deleting.has(selectedVmForDetails.id)}
                                  isStarting={selectedVmForDetails && loadingStates.starting.has(selectedVmForDetails.id)}
                                  isStopping={selectedVmForDetails && loadingStates.stopping.has(selectedVmForDetails.id)}
                                  isRebooting={selectedVmForDetails && loadingStates.rebooting.has(selectedVmForDetails.id)}
                                  isCreatingSnapshot={selectedVmForDetails && loadingStates.creatingSnapshot.has(selectedVmForDetails.id)}
                                  canStart={canStartVM}
                                  canStop={canStopVM}
                                  canReboot={canRebootVM}
                                  canHibernate={canHibernateVM}
                                  canRestore={canRestoreVM}
                                  canDelete={canDeleteVM}
                                  lockedVMs={lockedVMs}
                                />
                                
                                <VMPricingSection
                                  selectedVmForDetails={selectedVmForDetails}
                                  calculateVmPricing={calculateVmPricing}
                                  isAuthenticated={isAuthenticated}
                                />
                            </div>
                            
                            <div className="dashboard-sidebar" style={{ background: 'transparent', backgroundColor: 'transparent', borderLeft: 'none' }}>
                                <div className="sidebar-upper" style={{ marginTop: "-0.1cm", background: 'transparent' }}>
                               <AccountInfoSection
                                    userEmail={userEmail}
                                    barrackId={barrackId}
                                    creditBalance={creditBalance}
                                    userCurrency={userCurrency}
                                    creditBalanceError={creditBalanceError}
                                    handleShowCreditsModal={() => {
                                      logUserAction('credits_modal_opened', {
                                        source: 'account_info_section',
                                        currentBalance: creditBalance
                                      });
                                      setShowCreditsModal(true);
                                    }}
                                    navigate={navigate}
                                    isAuthenticated={isAuthenticated}
                                  />
                                  
                                  <QuickActionsSection 
                                    navigate={navigate} 
                                    isAuthenticated={isAuthenticated}
                                  />
                                  
                                  <LogoutSection 
                                    handleLogout={handleLogout}
                                    isAuthenticated={isAuthenticated}
                                  />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                // Mobile/Tablet layout
                <div className="min-h-screen bg-black text-white relative overflow-hidden">
                    <div className="absolute top-20 right-[10%] w-96 h-96 bg-[#1a1a1a] opacity-25 rounded-full blur-3xl"></div>
                    <div className="absolute -top-10 -left-20 w-72 h-72 bg-[#1a1a1a] opacity-20 rounded-full blur-3xl"></div>
                    
                    <div className="absolute bottom-0 left-0 w-full h-[70%] bg-[#cad9e3] opacity-[0.07] rounded-t-[100%] blur-3xl"></div>
                    
                    <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] z-0"></div>
                    
                    <div className={`relative z-10 space-y-4 ${isMobile ? 'px-1 py-4' : 'p-4'}`}>
                        <VMDetailsSection
                            selectedVmForDetails={selectedVmForDetails}
                            getStatusBadgeClass={getStatusBadgeClass}
                            getStatusTextColor={getStatusTextColor}
                            calculateVmPricing={calculateVmPricing}
                            navigate={navigate}
                            isAuthenticated={isAuthenticated}
                            lockedVMs={lockedVMs}
                        />
                        
                        <VMSelectorSection
                            vms={vms}
                            selectedVmForDetails={selectedVmForDetails}
                            vmSelectorDropdownOpen={vmSelectorDropdownOpen}
                            setVmSelectorDropdownOpen={setVmSelectorDropdownOpen}
                            handleVmSelect={handleVmSelect}
                            getStatusTextColor={getStatusTextColor}
                            navigate={navigate}
                            isAuthenticated={isAuthenticated}
                        />
                        
                        <VMActionsSection
                            selectedVmForDetails={selectedVmForDetails}
                            handleViewDetails={handleViewDetails}
                            handleHibernateClick={handleHibernateClick}
                            handleRestoreClick={handleRestoreClick}
                            setShowDeleteModal={setShowDeleteModal}
                            handleTakeSnapshotClick={handleTakeSnapshotClick}
                            handleCreateVolumeClick={handleCreateVolumeClick}
                            handleStartClick={handleStartClick}
                            handleStopClick={handleStopClick}
                            handleRebootClick={handleRebootClick}
                            isAuthenticated={isAuthenticated}
                            isHibernating={selectedVmForDetails && loadingStates.hibernating.has(selectedVmForDetails.id)}
                            isRestoring={selectedVmForDetails && loadingStates.restoring.has(selectedVmForDetails.id)}
                            isDeleting={selectedVmForDetails && loadingStates.deleting.has(selectedVmForDetails.id)}
                            isStarting={selectedVmForDetails && loadingStates.starting.has(selectedVmForDetails.id)}
                            isStopping={selectedVmForDetails && loadingStates.stopping.has(selectedVmForDetails.id)}
                            isRebooting={selectedVmForDetails && loadingStates.rebooting.has(selectedVmForDetails.id)}
                            isCreatingSnapshot={selectedVmForDetails && loadingStates.creatingSnapshot.has(selectedVmForDetails.id)}
                            canStart={canStartVM}
                            canStop={canStopVM}
                            canReboot={canRebootVM}
                            canHibernate={canHibernateVM}
                            canRestore={canRestoreVM}
                            canDelete={canDeleteVM}
                            lockedVMs={lockedVMs}
                        />
                        
                        <VMPricingSection
                            selectedVmForDetails={selectedVmForDetails}
                            calculateVmPricing={calculateVmPricing}
                            isAuthenticated={isAuthenticated}
                        />
                        
                     <AccountInfoSection
                            userEmail={userEmail}
                            barrackId={barrackId}
                            creditBalance={creditBalance}
                            userCurrency={userCurrency}
                            creditBalanceError={creditBalanceError}
                            handleShowCreditsModal={() => {
                              logUserAction('credits_modal_opened', {
                                source: 'mobile_account_info',
                                currentBalance: creditBalance
                              });
                              setShowCreditsModal(true);
                            }}
                            navigate={navigate}
                            isAuthenticated={isAuthenticated}
                        />
                        
                        <QuickActionsSection 
                            navigate={navigate} 
                            isAuthenticated={isAuthenticated}
                        />
                        
                        <LogoutSection 
                            handleLogout={handleLogout}
                            isAuthenticated={isAuthenticated}
                        />
                    </div>
                </div>
            )}
          
            {isAuthenticated && (
                <CreditsModal 
                    isOpen={showCreditsModal}
                    onClose={() => {
                      setShowCreditsModal(false);
                      logUserAction('credits_modal_closed');
                      logInfo('Credits modal closed');
                    }}
                    updateBalance={fetchCreditBalance}
                />
            )}

            <SnapshotConfirmationModal
                isOpen={selectedVmForSnapshot !== null}
                onClose={() => {
                  setSelectedVmForSnapshot(null);
                  logUserAction('snapshot_modal_closed');
                  logInfo('Snapshot modal closed');
                }}
                onConfirm={handleSnapshotConfirm}
                vm={selectedVmForSnapshot}
                isCreating={selectedVmForSnapshot && loadingStates.creatingSnapshot.has(selectedVmForSnapshot.id)}
                existingSnapshotNames={existingSnapshotNames}
            />

            <HibernateConfirmationModal
                isOpen={selectedVmForHibernate !== null}
                onClose={() => {
                  setSelectedVmForHibernate(null);
                  logUserAction('hibernate_modal_cancelled', {
                    vmId: selectedVmForHibernate?.id,
                    vmName: selectedVmForHibernate?.name
                  });
                }}
                onConfirm={handleHibernateVM}
                vm={selectedVmForHibernate}
                isHibernating={selectedVmForHibernate && loadingStates.hibernating.has(selectedVmForHibernate.id)}
            />

            <RestoreConfirmationModal
                isOpen={selectedVmForRestore !== null}
                onClose={() => {
                  setSelectedVmForRestore(null);
                  logUserAction('restore_modal_cancelled', {
                    vmId: selectedVmForRestore?.id,
                    vmName: selectedVmForRestore?.name
                  });
                }}
                onConfirm={handleRestoreHibernatedVM}
                vm={selectedVmForRestore}
                isRestoring={selectedVmForRestore && loadingStates.restoring.has(selectedVmForRestore.id)}
            />

            <StartConfirmationModal
                isOpen={selectedVmForStart !== null}
                onClose={() => {
                  setSelectedVmForStart(null);
                  logUserAction('start_modal_cancelled', {
                    vmId: selectedVmForStart?.id,
                    vmName: selectedVmForStart?.name
                  });
                }}
                onConfirm={handleStartVM}
                vm={selectedVmForStart}
                isStarting={selectedVmForStart && loadingStates.starting.has(selectedVmForStart.id)}
            />

            <StopConfirmationModal
                isOpen={selectedVmForStop !== null}
                onClose={() => {
                  setSelectedVmForStop(null);
                  logUserAction('stop_modal_cancelled', {
                    vmId: selectedVmForStop?.id,
                    vmName: selectedVmForStop?.name
                  });
                }}
                onConfirm={handleStopVM}
                vm={selectedVmForStop}
                isStopping={selectedVmForStop && loadingStates.stopping.has(selectedVmForStop.id)}
            />

            <RebootConfirmationModal
                isOpen={selectedVmForReboot !== null}
                onClose={() => {
                  setSelectedVmForReboot(null);
                  logUserAction('reboot_modal_cancelled', {
                    vmId: selectedVmForReboot?.id,
                    vmName: selectedVmForReboot?.name
                  });
                }}
                onConfirm={handleRebootVM}
                vm={selectedVmForReboot}
                isRebooting={selectedVmForReboot && loadingStates.rebooting.has(selectedVmForReboot.id)}
            />

            <CreateVolumeModal
                isOpen={showCreateVolumeModal}
                onClose={() => {
                  setShowCreateVolumeModal(false);
                  logUserAction('create_volume_modal_closed');
                  logInfo('Create volume modal closed');
                }}
                onSave={handleVolumeCreate}
                regions={regions}
                images={osImages}
                existingVolumeNames={existingVolumeNames}
            />

            <ConfirmationModal 
                isOpen={showDeleteModal}
                onClose={() => {
                  setShowDeleteModal(false);
                  logUserAction('delete_modal_cancelled', {
                    vmId: selectedVmForDetails?.id,
                    vmName: selectedVmForDetails?.name
                  });
                }}
                onConfirm={handleDeleteSelectedVM}
                isLoading={selectedVmForDetails && loadingStates.deleting.has(selectedVmForDetails.id)}
                    vm={selectedVmForDetails}  // ✅ ADD THIS LINE

            />

            {showDeleteNotification && (
                <div className="fixed bottom-4 right-4 bg-[#1a1a1a] text-white p-4 rounded-md border border-[#cad9e3]/30 shadow-lg z-50">
                  <p className="text-sm flex items-center">
                    <span className="animate-spin mr-2">⟳</span>
                    Deleting VM... Please wait.
                  </p>
                </div>
            )}
          
  
        {showVMDetails && selectedVM && (
    <div className="modal-overlay" style={{ zIndex: 1000 }}>
      <div className="modal-content vm-details-modal" style={{ width: '90%', maxWidth: '1200px', height: '90vh', overflow: 'auto' }}>
        <VMDetailsPage
          vmId={selectedVM.instance_id}  // ✅ CHANGE from selectedVM.id
          onBack={() => {
            logUserAction('vm_details_modal_closed', {
              vmId: selectedVM.instance_id,  // ✅ CHANGE from selectedVM.id
              vmName: selectedVM.name
            });
            logInfo('VM details modal closed');
            
            setShowVMDetails(false);
            setTimeout(() => {
              if (isAuthenticated) {
                fetchBarrackVMs(); 
              }
            }, 300);
          }}
          userCurrency={userCurrency}
        />
      </div>
    </div>
)}
        </ResponsiveWrapper>
    );
};

export default Dashboard;